<template>
	<div>
		<div class="section demo background-photo first sticky" id="openingBackground">
			<div class="spacer s1"></div>
			<h1 id="titleText"><span>The</span> Return <span>of</span> Global<br>Russia</h1>
			<div class="putin"></div>
		</div>
		<div class="section section foreground" id="introText">
			<div class="introText">
				<p>For much of the post–Cold War era, Russia’s ability to project its influence on a global scale was
					constrained by internal challenges and limited resources. But since Vladimir Putin returned to the
					presidency in 2012, Moscow has engaged in a broad campaign to expand its international reach.</p>
				<p>Putin has launched a Russian-style charm offensive in far-flung locales where the Kremlin’s influence had
					been all but written off. Russia’s presence is increasingly visible throughout the Middle East and in
					parts of Africa, Asia, Europe, and Latin America. At the same time, Moscow has found numerous openings
					and is busily exploiting divisions within Western societies. </p>
			</div>
		</div>
		<div class="section background white">
			<div class="section foreground expanding" id="expandingText">
				<div class="icon icon-tools-top"></div>
				<p>Russia’s agenda is straightforward: to assert its influence at the expense of Washington and the
					rules-based international system. The Kremlin’s toolkit includes: leveraging economic and business ties,
					exerting political influence, harnessing the information space, and forging or deepening military ties
					with key countries. Where the United States and its allies have pulled back or failed to deliver, Russia
					has eagerly stepped in.</p>
			</div>
		</div>
		<div class="section fx--on-the-march background">
			<div class="section popcorns">
				<div class="callout callout-first">
					<div class="callout-text">
						<div class="content-wrap">When people think about Russia’s disruptive <em
								class="highlighting first lit">foreign policy agenda</em>, they tend to focus on <em
								class="highlighting second lit">only a few</em> recent cases.</div>
					</div>
					<div class="fx--few-areas" id="calloutFewAreas">
						<div class="popcorn-pieces start popcorn-animate cols no-gutters">
							<div class="pops pop-syria col col-33" id="syria"
								style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Syria-138301017.jpg);">
								<span class="content-wrap">
									<span class="container">Intervening in<em>Syria</em></span>
								</span>
							</div>
							<div class="pops pop-ukraine col col-33" id="ukraine"
								style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Ukraine-463941130.jpg);">
								<span class="content-wrap">
									<span class="container">Invading <em>Ukraine</em></span>
								</span>
							</div>
							<div class="pops pop-hacking col col-33" id="hacking"
								style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Hacking-621678752.jpg);">
								<span class="content-wrap">
									<span class="container">Interfering in <em>Elections</em></span>
								</span>
							</div>
						</div>
					</div>
				</div>
				<div class="section callout callout-second" id="calloutTooNarrow">
					<div class="callout" id="calloutTooNarrowStick">
						<div class="callout-text">
							<div class="content-wrap">But Russia is actually casting <em class="highlighting third lit">a
									far broader net</em> that is having an increasingly <em
									class="highlighting fourth lit">global impact</em>.</div>
						</div>
						<div class="fx--few-areas longer-list" id="calloutTheRest">
							<div class="popcorn-pieces popcorn-animate top cols no-gutters">
								<div class="pops pop-rtsputnik popcorn-animate col col-25" id="restRtSputnik"
									style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Propaganda-872801178.jpg);">
									<span class="content-wrap">
										<span class="container">Building a global state-sponsored propaganda apparatus
										</span>
									</span>
								</div>
								<div class="pops pop-venezuela popcorn-animate col col-25" id="restVenezuela"
									style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Venezuela-584265588.jpg);">
									<span class="content-wrap">
										<span class="container">Propping up the Venezuelan government </span>
									</span>
								</div>
								<div class="pops pop-divisions popcorn-animate col col-25" id="restDivisions"
									style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Separatist-887793442.jpg);">
									<span class="content-wrap">
										<span class="container">Exploiting political divisions in Europe through social
											media and other tools</span>
									</span>
								</div>
								<div class="pops pop-mexico popcorn-animate col col-25" id="restMexico"
									style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Mexico-891083816.jpg);">
									<span class="content-wrap">
										<span class="container">Leveraging information operations to influence Mexico’s 2018
											presidential elections</span>
									</span>
								</div>
								<div class="pops pop-populist popcorn-animate col col-25" id="restPopulist"
									style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Populist-657228186.jpg);">
									<span class="content-wrap">
										<span class="container">Embracing populist and far right movements in Europe</span>
									</span>
								</div>
								<div class="pops pop-arms popcorn-animate col col-25" id="restArms"
									style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Arms-837208142.jpg);">
									<span class="content-wrap">
										<span class="container">Using major arms sales to undermine key U.S. alliance
											relationships</span>
									</span>
								</div>
								<div class="pops pop-africa popcorn-animate col col-25" id="restAfrica"
									style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Africa-842497708.jpg);">
									<span class="content-wrap">
										<span class="container">Fueling high-level corruption in South Africa </span>
									</span>
								</div>
								<div class="pops pop-balkans popcorn-animate col col-25" id="restBalkans"
									style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Balkans-621634232.jpg);">
									<span class="content-wrap">
										<span class="container">Stoking ethnic tensions and instability in the
											Balkans</span>
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="section callout callout-third" id="calloutOnTheMarch">
					<div class="callout" id="calloutOnTheMarchStick">
						<div class="content-wrap">Russia’s more <em class="highlighting fifth lit">assertive foreign
								policy</em> is making the Kremlin an important player in an <em
								class="highlighting sixth lit">expanding array</em> of countries and regions.</div>
					</div>
				</div>
			</div>
		</div>
		<div class="section tools" id="toolbox">
			<h2 class="center gutter-bottom" id="toolboxIntro">New Toolbox<br>Old Tools</h2>
			<div class="foreground center">
				<p>Since Russia’s economy recovered from its post–Cold War collapse, its foreign policy and security
					apparatus has commanded ever greater resources. The resulting toolkit that the Kremlin employs was first
					honed and perfected in Russia’s immediate neighborhood.</p>
				<p>Moscow’s global toolkit can be broken down into four broad sets of overlapping tools: </p>
				<ul class="clean-list cols">
					<li class="col col-25 economic">
						<a href="/publications/interactive/global-russia/economic">Economic Influence</a>
					</li>
					<li class="col col-25 political">
						<a href="/publications/interactive/global-russia/political">Political Influence</a>
					</li>
					<li class="col col-25 information">
						<a href="/publications/interactive/global-russia/digital">Information Space</a>
					</li>
					<li class="col col-25 military">
						<a href="/publications/interactive/global-russia/military">Military Ties</a>
					</li>
				</ul>
			</div>
		</div>
		<div class="section economy-tool">
			<div class="tool-chapter" id="EconomicTool">
				<div class="panel-container economic">
					<div class="chapter chapter-title economic-levers" id="EconomicLevers">
						<div class="title-container">
							<h2>Levers of Economic Influence</h2>
							<p>Moscow has employed economic tools such as preferential trade arrangements, discounts on oil
								and gas exports, debt relief, and financial bailouts to increase its global influence. All
								three components of the Russian energy sector (oil, gas, and nuclear power) often play
								leading roles in Russian diplomatic and commercial outreach. Links between government
								entities and criminal/corrupt activities are a standard feature of such efforts.</p>
						</div>
					</div>
					<div class="section chapter top-section case-study casestudy--venezuela" id="caseStudyVenezuela">
						<div class="foreground">
							<div class="icon icon-chapter-top">
								<h3>The Case of Venezuela</h3>
								<p>The Kremlin is now a key player in the dramatic events unfolding in Venezuela. Amid an
									increasingly desperate economic and humanitarian situation and sharpening tensions
									between Caracas and Washington, Moscow is helping prop up the beleaguered government of
									President Nicolás Maduro. In exchange, Russia is acquiring valuable assets at knockdown
									prices.</p>
							</div>
						</div>
					</div>
					<div class="photo-section" id="caseStudyStart">
						<div class="case-study-container-background" id="slideContainerBackground">
							<div></div>
						</div>
						<div class="case-study-slides venezuela" id="slideContainer">
							<div class="slideshow-hor slide1 item">
								<div class="image-container no-image">
									<span class="content-wrap">
										<span>Russia’s close ties to Venezuela date back to early in Putin’s tenure but have
											been <span class="highlight">recently energized</span> by a wave of U.S.
											sanctions against the Maduro regime and <a
												href="http://www.cnn.com/2017/08/11/politics/us-venezuela-trump-military-options/index.html"
												target="_blank">threats by President Donald Trump</a> to intervene
											militarily in Venezuela.</span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide2 item">
								<div class="image-container">
									<div class="img img2"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Venezuela2-169379446.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>State-controlled Russian oil giant Rosneft has <a
												href="https://www.reuters.com/article/us-venezuela-russia/venezuela-russias-rosneft-agree-on-14-billion-oil-gas-investment-idUSKBN0OC2YF20150528"
												target="_blank">invested heavily</a> in Venezuela’s oil and gas sector.<div
												class="slide-caption">Venezuelan state oil company PDVSA’s former president
												Rafael Ramírez (center) with Rosneft CEO Igor Sechin (second from right)
											</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide3 item">
								<div class="image-container">
									<div class="img img3"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Venezuela3-584265588.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span><span class="highlight">Billions of dollars</span> in Russian advance payments
											for deliveries of Venezuelan crude have helped Maduro hold on to power.<div
												class="slide-caption">Rosneft CEO Igor Sechin (left) with Venezuelan
												President Nicolás Maduro (right)</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide4 item">
								<div class="image-container">
									<div class="videoEmbed"><iframe class="b-lazy" width="560" height="315"
											src="https://www.youtube.com/embed/OAOcnC--Haw?rel=0&amp;start=187&amp;end=205"
											frameborder="0" gesture="media" allow="encrypted-media"
											allowfullscreen=""></iframe></div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span>Russian financial support is <span
												class="highlight">critical</span> as Venezuela copes with the effects of a
											chaotic default, hyperinflation, and economic collapse.</span></span>
								</div>
							</div>
							<div class="slideshow-hor slide5 item">
								<div class="image-container">
									<div class="img img5"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Venezuela5-827743882.jpg)">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span>Russian debt relief and large shipments of wheat have
											helped the Maduro regime contend with <span class="highlight">chronic food
												shortages</span>.<div class="slide-caption">Graffiti in Caracas reading “We
												are hungry” and “Maduro dictator”</div></span></span>
								</div>
							</div>
							<div class="slideshow-hor slide6 item">
								<div class="image-container">
									<div class="img img6"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Venezuela6-172120460.jpg)">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span>In November 2017, the Russian government <a
												href="https://www.reuters.com/article/russia-venezuela-debt-details/update-1-russia-restructures-venezuelan-debt-pdvsa-loans-not-included-idUSL8N1NL4F8"
												target="_blank">agreed</a> to restructure <span
												class="highlight">approximately $3 billion</span> in loans to help ease
											Caracas’s debt burden. </span></span>
								</div>
							</div>
						</div>
					</div>
					<div class="section chapter conclusion">
						<div class="foreground">
							<div class="icon icon-chapter-top"></div>
							<p>Russia’s activities in Venezuela are just one example of how Moscow is leveraging its
								economic power to expand its influence in the Western Hemisphere. Rosneft’s growing role in
								Venezuela’s energy sector illustrates how Russian political and business leaders often blend
								commercial opportunities with important foreign policy objectives.</p>
						</div>
					</div>
					<div class="section map map-energy" id="energyStartSection">
						<h3 id="energyStart">Select Russian energy deals around the world </h3>
					</div>
					<div id="energyStick">
						<div class="slide energy energy-slide6 b-lazy b-loaded slide--active" id="energySlide6"
							style="background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-energy6-end.gif); opacity: 1;">
						</div>
					</div>
					<div class="section map map-energy map-captions">
						<div class="caption-list">
							<h4>Venezuela</h4>
							<p>Rosneft has stakes in five major oil projects and purchased two new offshore gas blocks in
								2017. Venezuela owes Rosneft $6 billion.</p>
							<p class="source">Source: <a
									href="https://www.bloomberg.com/news/articles/2017-11-03/venezuela-restructuring-heightens-rosneft-s-6-billion-risk"
									target="_blank">Bloomberg</a></p>
							<h4>South Africa</h4>
							<p>Rosatom, Russia’s state-owned atomic energy firm, signed an agreement in 2014 to build
								nuclear reactors worth $76 billion in South Africa.</p>
							<p class="source">Source: <a href="http://www.newsweek.com/south-africa-nuclear-deal-590035"
									target="_blank">Newsweek</a></p>
							<h4>Turkey</h4>
							<p>Rosatom began construction on the $20 billion Akkuyu Nuclear Power Plant in 2017.</p>
							<p class="source">Source: <a
									href="http://www.akkunpp.com/akkuyu-npp-construction-under-the-limited-construction-permit-started-in-turkey/update"
									target="_blank">Akkuyu Nükleer</a></p>
							<h4>Iraqi Kurdistan</h4>
							<p>Rosneft provided more than $4 billion in loans and investments to the Kurdish oil and gas
								sectors from 2016 to 2017.</p>
							<p class="source">Source: <a
									href="https://www.reuters.com/article/us-mideast-crisis-kurds-referendum-russi/russia-becomes-iraq-kurds-top-funder-quiet-about-independence-vote-idUSKCN1BV1IH"
									target="_blank">Reuters</a></p>
							<h4>India</h4>
							<p>Rosneft purchased Indian refiner Essar Oil in 2017 for $12.9 billion.</p>
							<p class="source">Source: <a
									href="https://www.economist.com/news/business/21724444-chastening-indian-conglomerate-what-rosnefts-purchase-essars-oil-refinery-means"
									target="_blank">Economist</a></p>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="section economy-tool">
			<div class="tool-chapter" id="EconomicTool">
				<div class="panel-container economic">
					<div class="chapter chapter-title economic-levers" id="EconomicLevers">
						<div class="title-container">
							<h2>Levers of Economic Influence</h2>
							<p>Moscow has employed economic tools such as preferential trade arrangements, discounts on oil
								and gas exports, debt relief, and financial bailouts to increase its global influence. All
								three components of the Russian energy sector (oil, gas, and nuclear power) often play
								leading roles in Russian diplomatic and commercial outreach. Links between government
								entities and criminal/corrupt activities are a standard feature of such efforts.</p>
						</div>
					</div>
					<div class="section chapter top-section case-study casestudy--venezuela" id="caseStudyVenezuela">
						<div class="foreground">
							<div class="icon icon-chapter-top">
								<h3>The Case of Venezuela</h3>
								<p>The Kremlin is now a key player in the dramatic events unfolding in Venezuela. Amid an
									increasingly desperate economic and humanitarian situation and sharpening tensions
									between Caracas and Washington, Moscow is helping prop up the beleaguered government of
									President Nicolás Maduro. In exchange, Russia is acquiring valuable assets at knockdown
									prices.</p>
							</div>
						</div>
					</div>
					<div class="photo-section" id="caseStudyStart">
						<div class="case-study-container-background" id="slideContainerBackground">
							<div></div>
						</div>
						<div class="case-study-slides venezuela" id="slideContainer">
							<div class="slideshow-hor slide1 item">
								<div class="image-container no-image">
									<span class="content-wrap">
										<span>Russia’s close ties to Venezuela date back to early in Putin’s tenure but have
											been <span class="highlight">recently energized</span> by a wave of U.S.
											sanctions against the Maduro regime and <a
												href="http://www.cnn.com/2017/08/11/politics/us-venezuela-trump-military-options/index.html"
												target="_blank">threats by President Donald Trump</a> to intervene
											militarily in Venezuela.</span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide2 item">
								<div class="image-container">
									<div class="img img2"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Venezuela2-169379446.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>State-controlled Russian oil giant Rosneft has <a
												href="https://www.reuters.com/article/us-venezuela-russia/venezuela-russias-rosneft-agree-on-14-billion-oil-gas-investment-idUSKBN0OC2YF20150528"
												target="_blank">invested heavily</a> in Venezuela’s oil and gas sector.<div
												class="slide-caption">Venezuelan state oil company PDVSA’s former president
												Rafael Ramírez (center) with Rosneft CEO Igor Sechin (second from right)
											</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide3 item">
								<div class="image-container">
									<div class="img img3"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Venezuela3-584265588.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span><span class="highlight">Billions of dollars</span> in Russian advance payments
											for deliveries of Venezuelan crude have helped Maduro hold on to power.<div
												class="slide-caption">Rosneft CEO Igor Sechin (left) with Venezuelan
												President Nicolás Maduro (right)</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide4 item">
								<div class="image-container">
									<div class="videoEmbed"><iframe class="b-lazy" width="560" height="315"
											src="https://www.youtube.com/embed/OAOcnC--Haw?rel=0&amp;start=187&amp;end=205"
											frameborder="0" gesture="media" allow="encrypted-media"
											allowfullscreen=""></iframe></div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span>Russian financial support is <span
												class="highlight">critical</span> as Venezuela copes with the effects of a
											chaotic default, hyperinflation, and economic collapse.</span></span>
								</div>
							</div>
							<div class="slideshow-hor slide5 item">
								<div class="image-container">
									<div class="img img5"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Venezuela5-827743882.jpg)">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span>Russian debt relief and large shipments of wheat have
											helped the Maduro regime contend with <span class="highlight">chronic food
												shortages</span>.<div class="slide-caption">Graffiti in Caracas reading “We
												are hungry” and “Maduro dictator”</div></span></span>
								</div>
							</div>
							<div class="slideshow-hor slide6 item">
								<div class="image-container">
									<div class="img img6"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Venezuela6-172120460.jpg)">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span>In November 2017, the Russian government <a
												href="https://www.reuters.com/article/russia-venezuela-debt-details/update-1-russia-restructures-venezuelan-debt-pdvsa-loans-not-included-idUSL8N1NL4F8"
												target="_blank">agreed</a> to restructure <span
												class="highlight">approximately $3 billion</span> in loans to help ease
											Caracas’s debt burden. </span></span>
								</div>
							</div>
						</div>
					</div>
					<div class="section chapter conclusion">
						<div class="foreground">
							<div class="icon icon-chapter-top"></div>
							<p>Russia’s activities in Venezuela are just one example of how Moscow is leveraging its
								economic power to expand its influence in the Western Hemisphere. Rosneft’s growing role in
								Venezuela’s energy sector illustrates how Russian political and business leaders often blend
								commercial opportunities with important foreign policy objectives.</p>
						</div>
					</div>
					<div class="section map map-energy" id="energyStartSection">
						<h3 id="energyStart">Select Russian energy deals around the world </h3>
					</div>
					<div id="energyStick">
						<div class="slide energy energy-slide6 b-lazy b-loaded slide--active" id="energySlide6"
							style="background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-energy6-end.gif); opacity: 1;">
						</div>
					</div>
					<div class="section map map-energy map-captions">
						<div class="caption-list">
							<h4>Venezuela</h4>
							<p>Rosneft has stakes in five major oil projects and purchased two new offshore gas blocks in
								2017. Venezuela owes Rosneft $6 billion.</p>
							<p class="source">Source: <a
									href="https://www.bloomberg.com/news/articles/2017-11-03/venezuela-restructuring-heightens-rosneft-s-6-billion-risk"
									target="_blank">Bloomberg</a></p>
							<h4>South Africa</h4>
							<p>Rosatom, Russia’s state-owned atomic energy firm, signed an agreement in 2014 to build
								nuclear reactors worth $76 billion in South Africa.</p>
							<p class="source">Source: <a href="http://www.newsweek.com/south-africa-nuclear-deal-590035"
									target="_blank">Newsweek</a></p>
							<h4>Turkey</h4>
							<p>Rosatom began construction on the $20 billion Akkuyu Nuclear Power Plant in 2017.</p>
							<p class="source">Source: <a
									href="http://www.akkunpp.com/akkuyu-npp-construction-under-the-limited-construction-permit-started-in-turkey/update"
									target="_blank">Akkuyu Nükleer</a></p>
							<h4>Iraqi Kurdistan</h4>
							<p>Rosneft provided more than $4 billion in loans and investments to the Kurdish oil and gas
								sectors from 2016 to 2017.</p>
							<p class="source">Source: <a
									href="https://www.reuters.com/article/us-mideast-crisis-kurds-referendum-russi/russia-becomes-iraq-kurds-top-funder-quiet-about-independence-vote-idUSKCN1BV1IH"
									target="_blank">Reuters</a></p>
							<h4>India</h4>
							<p>Rosneft purchased Indian refiner Essar Oil in 2017 for $12.9 billion.</p>
							<p class="source">Source: <a
									href="https://www.economist.com/news/business/21724444-chastening-indian-conglomerate-what-rosnefts-purchase-essars-oil-refinery-means"
									target="_blank">Economist</a></p>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="section political-tool">
			<div class="tool-chapter" id="PoliticalTool">
				<div class="panel-container political">
					<div class="chapter chapter-title political-levers" id="parallaxPoliticalContainer">
						<div class="title-container">
							<h2>Levers of Political Influence</h2>
							<p>Russia has long taken advantage of corrupt networks across Europe and Eurasia to reward
								businesses close to the Kremlin and build leverage with local political leaders. Moscow
								routinely promotes the interests of Russia-friendly politicians, bureaucrats, and business
								figures in neighboring states and uses Soviet-era ties with intelligence and military
								officials to further Russian political goals. </p>
						</div>
					</div>
					<div class="section chapter top-section case-study casestudy--hungary" id="caseStudyHungary">
						<div class="foreground">
							<div class="icon icon-chapter-top">
								<h3>The Case of Hungary</h3>
								<p>THungary’s slide into illiberalism under Prime Minister Viktor Orbán has stoked tensions
									between Budapest and the European Union (EU). Hungary’s estrangement from the European
									mainstream has created opportunities for Russia to expand its economic and political
									influence in the country. </p>
							</div>
						</div>
					</div>
					<div class="photo-section" id="caseStudyStartHungary">
						<div class="case-study-slides hungary" id="slideContainerHungary">
							<div class="slideshow-hor slide1 item">
								<div class="image-container">
									<div class="img img2"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Hungary1-462616577.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>Orbán’s ruling <span class="highlight">Fidesz Party</span> has done a
											180-degree turn and become <a
												href="https://index.hu/belfold/2010/11/05/orban_keleti_szel_fuj/"
												target="_blank">increasingly friendly</a> toward Russia. Meanwhile, the far
											right <span class="highlight">Jobbik Party</span> has allegedly received <a
												href="https://www.nytimes.com/2015/06/08/world/europe/russia-fights-wests-ukraine-sanctions-with-aid-and-ideology.html?_r=0"
												target="_blank">Russian financing</a>.<div class="slide-caption">Hungarian
												Prime Minister Viktor Orbán (left) with Russian President Vladimir Putin
												(right)
											</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide2 item">
								<div class="image-container">
									<div class="img img2"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Hungary2-105952902.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>Both Fidesz and Jobbik have expressed <span class="highlight">strong
												opposition</span> to EU sanctions against Russia.<div class="slide-caption">
												The parliament building in Budapest
											</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide3 item">
								<div class="image-container">
									<div class="img img3"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Hungary3-873239576.jpg);">
									</div>
								</div>
								<div class="slide-content quote">
									<span class="content-wrap">
										<span>“Non-economic problems cannot be solved with economic means . . . Everyone
											stands to lose from such solutions.”<div class="attribution">&mdash;<a
													href="https://www.ft.com/content/f1f4482a-e96b-11e6-893c-082c54a7f539"
													target="_blank">Prime Minister Orbán</a> on Western sanctions against
												Russia</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide4 item">
								<div class="image-container no-image">
									<span class="content-wrap">Warming relations between Hungary and Russia are reinforced
										by cozy <span class="highlight">energy and economic dealings</span>. Well-connected
										Russian firms like Gazprom have amassed considerable influence in Budapest over the
										past two decades. Hungary relies on Russian state-owned companies to fuel its <span
											class="highlight">nuclear power</span> plants, which generate almost half of the
										country’s electricity.</span>
								</div>
							</div>
							<div class="slideshow-hor slide5 item">
								<div class="image-container">
									<div class="img img5"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Hungary5-477681673.jpg)">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>In 2014, Hungary <a
												href="https://www.politico.eu/article/hungarys-russian-built-nuclear-plant-powered-by-politics-in-brussels/"
												target="_blank">awarded</a> Rosatom a <span class="highlight">$14 billion
												project</span> to upgrade and modernize the Paks II Nuclear Power Plant,
											<span class="highlight">without opening the project for competitive
												bidding</span>.
											<div class="slide-caption">Greenpeace protests Paks II on the Hungarian
												Parliament’s
												office building in Budapest</div>
										</span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide6 item">
								<div class="image-container no-image">
									<span class="content-wrap">Russian intelligence and security service operations
										reportedly have also <a
											href="https://www.politico.eu/article/moscow-spooks-return-to-hungary-raising-nato-hackles/"
											target="_blank">increased in Hungary</a> in recent years. In December 2017,
										Hungarian prosecutors charged Jobbik Party politician and Hungarian member of the
										European Parliament <a
											href="https://www.foreign.senate.gov/imo/media/doc/FinalRR.pdf"
											target="_blank">Bela Kovacs</a> with <span class="highlight">spying on the EU
											for Russia</span>.</span>
								</div>
							</div>
							<div class="slideshow-hor slide7 item">
								<div class="image-container">
									<div class="img img5"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/Hungary7-467856807.jpg)">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span><a href="https://www.foreign.senate.gov/imo/media/doc/FinalRR.pdf"
												target="_blank">Russian military intelligence</a> <span
												class="highlight">reportedly cultivated</span> the Hungarian National Front
											(MNA), a far-right militant group that actively sought to secure funding from
											Moscow. According to Hungary’s <a
												href="https://www.ft.com/content/66d3993a-b0b8-11e6-9c37-5787335499a0"
												target="_blank">National Security Committee</a>, MNA members conducted
											military-style training with <a href="https://euobserver.com/opinion/136354"
												target="_blank">Russian Foreign Military Intelligence (GRU)</a> officers
											working
											in Hungary under diplomatic cover.
											<div class="slide-caption">Hungarian National Front members march with the MNA
												flag
												in Budapest in February 2014</div>
										</span>
									</span>
								</div>
							</div>
						</div>
					</div>
					<div class="section chapter conclusion">
						<div class="foreground">
							<div class="icon icon-chapter-top"></div>
							<p>The Orbán government’s business dealings with Moscow have exacerbated frictions with the EU
								and key European countries such as Germany and France. Russian intelligence and security
								service operations in Hungary have also caused concern among its EU and NATO partners. Orbán
								is an important standard-bearer for anti-EU populist sentiment in Europe. His pro-Russian,
								anti-EU stance does face some resistance at home, however, as most Hungarians hold a <a
									href="http://www.pewglobal.org/2017/06/15/post-brexit-europeans-more-favorable-toward-eu/"
									target="_blank">positive view</a> of the EU. </p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="section digital-tool">
			<div class="tool-chapter" id="DigitalTool">
				<div class="panel-container digital">
					<div class="chapter chapter-title information-space" id="parallaxDigitalContainer">
						<div class="title-container">
							<h2>Information Space</h2>
							<p>The Kremlin frequently exploits new opportunities in the digital domain to promote narratives
								conducive to Russian interests and to undermine liberal Western governments. Moscow pushes
								these narratives via a variety of platforms, including traditional and social media;
								educational, cultural, and entertainment programs; and cyber-enabled information operations.
								In countries with Russian-speaking populations, Moscow-backed Russian-language media,
								including pop culture and entertainment programming, are powerful tools.</p>
						</div>
					</div>
					<div class="section chapter top-section case-study casestudy--eu" id="caseStudyEU">
						<div class="foreground">
							<div class="icon icon-chapter-top">
								<h3>Information Operations:<br />The Cases of Germany, the UK, and Spain</h3>
								<p>Throughout Europe, Moscow is using social media, state-controlled news outlets, and cyber
									operations to disrupt decisionmaking and promote societal divisions.</p>
							</div>
						</div>
					</div>
					<div class="photo-section" id="caseStudyStartEU">
						<div class="case-study-slides eu" id="slideContainer">
							<div class="slideshow-hor slide1 item">
								<div class="image-container">
									<div class="img img1"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/EU1-839477744.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>In Germany, Russian information operations have <span
												class="highlight">exploited internal tensions</span> over Chancellor Angela
											Merkel’s immigration policies and supported the radical nationalist Alternative
											for Deutschland (AfD) party and far-left Die Linke party.<div
												class="slide-caption">AfD supporters protest German Chancellor Angela
												Merkel’s immigration policies
											</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide2 item">
								<div class="image-container">
									<div class="videoEmbed"><iframe class="b-lazy" width="560" height="315"
											src="https://www.youtube.com/embed/kbIj-SwHjK4?rel=0&start=16&end=36"
											frameborder="0" gesture="media" allow="encrypted-media"
											allowfullscreen=""></iframe></div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>The most infamous episode of Russian disinformation involved <span
												class="highlight">a false story</span> in early 2016 about an ethnically
											Russian, thirteen-year-old German girl abducted by migrants.</span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide3 item">
								<div class="image-container">
									<div class="img img3"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/EU3-85278736.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>The story spread quickly by way of Russian state-controlled media outlets (RT
											and Sputnik), social media, and even <span class="highlight">Russian Foreign
												Minister Sergey Lavrov</span>.</span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide4 item">
								<div class="image-container">
									<div class="img img3"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/EU4-541190014.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>UK lawmakers are <span class="highlight">conducting an ongoing
												investigation</span> into whether the Kremlin sought to influence the
											outcome of the 2016 Brexit referendum.</span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide5 item">
								<div class="image-container">
									<div class="videoEmbed"><iframe class="b-lazy" width="560" height="315"
											src="https://www.youtube.com/embed/Gs5GQ7o5cFo?start=50&end=62" frameborder="0"
											gesture="media" allow="encrypted-media" allowfullscreen=""></iframe></div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>TBritish Prime Minister Theresa May in November 2017 publicly accused Russia
											of “<span class="highlight">weaponizing information</span>” and interfering in
											various Western election campaigns.</span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide6 item">
								<div class="image-container">
									<div class="img img6"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/EU6-893921224.jpg)">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span>Spain’s foreign and defense ministers <span
												class="highlight">accused Russian state and private groups</span> of trying
											to bolster support for the Catalonian referendum. These groups were reportedly
											aided by teams operating in Venezuela.</span></span>
								</div>
							</div>
						</div>
					</div>
					<div class="section chapter conclusion">
						<div class="foreground">
							<div class="icon icon-chapter-top"></div>
							<p>Moscow repeatedly has used new technologies and information as key elements of its foreign
								policy arsenal. These efforts have played on preexisting tensions and the vulnerability of
								open societies to foreign manipulation in an era of popular anger, fake news, and widespread
								dependence on social media. </p>
						</div>
					</div>
					<div class="section timeline-news" id="digitalConclusion">
						<h3 id="digitalTimelineTitle">Russia’s Growing State-Sponsored Propaganda Apparatus</h3>

						<div class="frst-timeline frst-timeline-style-8 frst-date-opposite frst-alternate"
							id="digitalTimeline">
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2005</span></div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT International TV is established</h2>
										<p class="details">24/7 news channel providing a “Russian viewpoint on major global
											events”</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2007</span></div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> Russia Beyond the Headlines is established </h2>
										<p class="details">Produced by Rossiyskaya Gazeta, the Kremlin’s paper of record,
											with
											content in thirteen languages</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT Arabic TV is established</h2>
										<p class="details">24/7 news channel in Arabic broadcasting from Moscow </p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT’s first YouTube channel is launched</h2>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2008</span></div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2>RT Arabic YouTube channel is launched</h2>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2009</span></div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2>RT en Español TV is established</h2>
										<p class="details">24/7 Spanish-language news channel broadcasting from Moscow with
											correspondents in Buenos Aires, Caracas, Havana, Madrid, Mexico City, Miami, and
											Washington, DC</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2>RT begins broadcasting in Canada</h2>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2>RT en Español YouTube channel is launched</h2>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2010</span></div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT America TV is established</h2>
										<p class="details">24/7 news channel broadcasting from Washington, DC</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT America YouTube channel is launched</h2>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2>Russia Today is rebranded as RT and begins “Question More” advertising campaign
											in
											the United States and UK</h2>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2011</span></div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT in Russian YouTube channel is launched</h2>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2013</span></div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RUPTLY is established</h2>
										<p class="details">RT-affiliated video news agency based in Berlin</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2>Rossiya Segodnya is established by Kremlin decree</h2>
										<p class="details">Led by pro-Kremlin media personality Dmitry Kiselyov, the
											state-run
											media conglomerate united RIA Novosti and Voice of Russia radio to increase
											Russia’s
											global media presence </p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2014</span></div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> Sputnik News replaces RIA Novosti </h2>
										<p class="details"> Sputnik News replaces RIA Novosti as Russia’s international news
											agency with content in over thirty languages (including Abkhaz, Arabic,
											Armenian,
											Azerbaijani, Belarusian, Chinese, Czech, Dari, English, Estonian, French,
											Georgian,
											German, Italian, Japanese, Kazakh, Kurdish, Kyrgyz, Latvian, Lithuanian,
											Moldovan,
											Ossetian, Persian, Polish, Portuguese, Serbian, Spanish, Tajik, Turkish, Uzbek,
											and
											Vietnamese)</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT UK TV is established</h2>
										<p class="details">24/7 news channel broadcasting from London</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT Deutsch is established</h2>
										<p class="details">RT’s German-language online platform</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT France, RT UK, and RT Deutsch YouTube channels are launched</h2>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2015</span></div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT en Français is established</h2>
										<p class="details">RT’s French-language online platform</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2>RT Chinese YouTube channel is launched</h2>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2017</span></div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT France TV is established</h2>
										<p class="details">24/7 news channel broadcasting from Paris</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"><span>2018</span></div>
							</div>
							<div class="frst-timeline-block frst-odd-item" data-animation="slideInUp">
								<div class="frst-timeline-img"><span></span></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner">
										<h2> RT Deutsch TV to be established</h2>
										<p class="details">News channel planned as early as 2018</p>
									</div>
								</div>
							</div>
							<div class="frst-timeline-block frst-timeline-label-block" data-animation="slideInUp">
								<div class="frst-labels frst-start-label"></div>
							</div>
							<div class="frst-timeline-block frst-even-item" data-animation="slideInUp">
								<div class="frst-timeline-img"></div>
								<div class="frst-timeline-content swing" style="position: relative;">
									<div class="frst-timeline-content-inner"></div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="section military-tool">
			<div class="tool-chapter" id="MilitaryTool">
				<div class="panel-container military">
					<div class="chapter chapter-title military-security" id="parallaxMilitaryContainer">
						<div class="title-container">
							<h2>Military and Security Ties</h2>
							<p>Russia has taken advantage of instability throughout the Middle East and U.S. retrenchment to rebuild ties with governments and regimes across the region. These efforts include: arms sales, military training and cooperation, and technology transfers.</p>
						</div>
					</div>
					<div class="section chapter top-section case-study casestudy--mena" id="caseStudyMENA">
						<div class="foreground">
							<div class="icon icon-chapter-top">
								<h3>The Cases of Syria, Libya, Egypt, and Saudi Arabia</h3>
								<p>Moscow has capitalized on vacuums created by U.S. policy mistakes in the Middle East and Washington’s retreat from global leadership. Russia has expanded military relationships with a variety of counterparts in the region.</p>
							</div>
						</div>
					</div>
					<div class="photo-section" id="caseStudyMENAStart">
						<div class="case-study-container-background" id="caseStudyMENAStart">
							<div></div>
						</div>
						<div class="case-study-slides mena" id="slideMENAContainer">
							<div class="slideshow-hor slide1 item">
								<div class="image-container">
									<div class="videoEmbed"><iframe class="b-lazy" width="560" height="315"
											src="https://www.youtube.com/embed/jh-ALjytWtA?rel=0&start=10&end=25"
											frameborder="0" gesture="media" allow="encrypted-media"
											allowfullscreen=""></iframe></div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span> Russia’s decisive military intervention in Syria, beginning in September 2015, has spurred a dramatic series of <span class="highlight">diplomatic openings</span> with other Middle Eastern states.</span></span>
								</div>
							</div>
							<div class="slideshow-hor slide2 item">
								<div class="image-container">
									<div class="img img2"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/MENA2-111507954.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>Amid an increasingly chaotic situation in Libya, the Kremlin is building a <span class="highlight">partnership with strongman General Khalifa Haftar</span> and cultivating ties with other competing groups.<div
												class="slide-caption">A pro-Kremlin youth group demands, “NATO, stop destroying Libya!”
											</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide3 item">
								<div class="image-container">
									<div class="videoEmbed"><iframe class="b-lazy" width="560" height="315"
											src="https://www.youtube.com/embed/detwaFjVi9M?rel=0&start=01&end=18"
											frameborder="0" gesture="media" allow="encrypted-media"
											allowfullscreen=""></iframe></div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span> In January 2017, General Haftar <a href="https://www.reuters.com/article/us-libya-security-russia/russia-turns-to-libya-with-show-of-support-for-eastern-commander-idUSKBN1510KT" target="_blank">visited</a> the Russian aircraft carrier Admiral Kuznetsov off the Libyan coast, a <span class="highlight">public show of Moscow’s support</span>.</span></span>
								</div>
							</div>
							<div class="slideshow-hor slide4 item">
								<div class="image-container">
									<div class="img img2"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/MENA4-626376686.jpg);">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap">
										<span>Moscow has reportedly <a href="https://www.reuters.com/article/us-usa-libya-russia/link-seen-between-russia-and-libyan-commander-haftar-u-s-general-idUSKBN16V2IW" target="_blank">sent</a> special forces to Egypt near Libya’s border and <span class="highlight">provided various types of support</span> to Haftar’s forces.<div
												class="slide-caption">Libya’s General Haftar leaving the Russian Ministry of Foreign Affairs
											</div></span>
									</span>
								</div>
							</div>
							<div class="slideshow-hor slide5 item">
								<div class="image-container">
									<div class="img img5"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/MENA5-463070190.jpg)">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span>In Egypt, the Kremlin has courted President Abdel Fattah el-Sisi and reportedly has reached a deal on <span class="highlight">access to Egyptian airspace and airfields</span>.<div class="slide-caption">A billboard in Cairo of Presidents Putin (left) and Sisi (right)</div></span></span>
								</div>
							</div>
							<div class="slideshow-hor slide6 item">
								<div class="image-container">
									<div class="img img6"
										style="background-image: url(https://s3.amazonaws.com/ceipimg/GlobalRussia/MENA6-840864004.jpg)">
									</div>
								</div>
								<div class="slide-content">
									<span class="content-wrap"><span>In October 2017, during King Salman’s historic visit to Moscow—<span class="highlight">the first by a sitting Saudi monarch to Russia</span>—Riyadh agreed to <a href="https://www.nytimes.com/2017/10/05/world/middleeast/saudi-russia-middle-east.html" target="_blank">purchase</a> an <span class="highlight">S-400 air defense system</span> and other weapons.<div class="slide-caption">A Russian S-400 system</div></span></span>
								</div>
							</div>
							<div class="slideshow-hor slide7 item">
								<div class="image-container no-image">
									<span class="content-wrap"><span>Saudi Arabia and Russia signed various documents on energy, trade, and defense cooperation. The two countries agreed on a $3 billion <a href="http://www.bbc.com/news/world-europe-41512759" target="_blank">arms deal</a> and several billion dollars in joint investment projects.</span></span>
								</div>
							</div>
						</div>
					</div>
					<div class="section chapter conclusion">
						<div class="foreground">
							<div class="icon icon-chapter-top"></div>
							<p>Russia’s military power is most visible in the Middle East, where it has established its ability to deploy an expeditionary force and serve as a party to conflict resolution efforts. As the world’s second largest arms exporter, Russia has aggressively pursued weapons sales around the world for financial gain and to project its political, economic, and military clout. </p>
						</div>
					</div>
					<div class="section map map-arms" id="armsStartSection">
						<h3 id="energyStart">Arms sales around the world</h3>
					</div>
					<div id="energyStick">
						<div class="slide energy energy-slide6 b-lazy b-loaded slide--active" id="energySlide6"
							style="background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-arms9-end.gif); opacity: 1;">
						</div>
					</div>
					<div class="section map map-energy map-captions">
						<div class="caption-list">
							<h4>Peru</h4>
							<p><strong>Order</strong> 24 Mi-171Sh military transport helicopters</p>
							<p><strong>Status</strong> Deliveries completed (2015)</p>
							<p class="source">Source: <a href="http://tass.com/defense/866023">tass.com</a></p>
							<h4>Venezuela</h4>
							<p><strong>Order</strong> Equipment including Pechora-2M, S-300, and Buk-M2EK anti-aircraft complexes; Igla-S missile systems; BTR-80A armored personnel carriers, T-72 tanks; and Smerch and Grad rocket launchers</p>
							<p><strong>Status</strong> Deliveries completed (2017)</p>
							<p class="source">Source: <a href="https://ria.ru/economy/20160329/1399129319.html" target="_blank">ria.ru</a>, <a href="http://tass.com/defense/978893" target="_blank">tass.com</a></p>
							<h4>Algeria</h4>
							<p><strong>Order</strong> 14 Su-30 fighter jets</p>
							<p><strong>Status</strong> Deliveries started (2017)</p>
							<p class="source">Source: <a href="https://bmpd.livejournal.com/2355958.html" target="_blank">bmpd.livejournal.com</a></p>
							<h4>Nigeria</h4>
							<p><strong>Order</strong> 12 Mi-35M attack helicopters</p>
							<p><strong>Status</strong> Deliveries started (2017)</p>
							<p class="source">Source: <a href="https://ria.ru/defense_safety/20170626/1497284437.html" target="_blank">ria.ru</a></p>
							<h4>Turkey</h4>
							<p><strong>Order</strong> 4 S-400 anti-aircraft missile systems</p>
							<p><strong>Status</strong> Contract signed (2017)</p>
							<p class="source">Source: <a href="https://www.nytimes.com/2017/12/29/world/europe/turkey-russia-arms-deal.html" target="_blank">nytimes.com</a></p>
							<h4>Iraq</h4>
							<p><strong>Order</strong> Several hundred tanks, including a first batch of 73 T-90 tanks</p>
							<p><strong>Status</strong> Deliveries started (2017)</p>
							<p class="source">Source: <a href="https://iz.ru/621346/egor-sozaev-gurev/v-kremle-podtverdili-prodazhu-t-90-iraku" target="_blank">iz.ru</a>, <a href="http://tass.ru/armiya-i-opk/4730131" target="_blank">tass.ru</a></p>
							<h4>Vietnam</h4>
							<p><strong>Order</strong> 64 T-90 tanks</p>
							<p><strong>Status</strong> Deliveries started (2017)</p>
							<p class="source">Source: <a href="http://www.interfax.ru/world/586352">interfax.ru</a>; <a href="https://lenta.ru/news/2017/07/25/viet/" target="_blank">lenta.ru</a></p>
							<h4>Indonesia</h4>
							<p><strong>Order</strong> 11 Su-35 fighter jets</p>
							<p><strong>Status</strong> Contract signed (2018)</p>
							<p class="source">Source: <a href="https://www.reuters.com/article/us-indonesia-russia-jets/indonesia-to-buy-1-14-billion-worth-of-russian-jets-idUSKCN1B20QM" target="_blank">reuters.com</a></p>
							<h4>&nbsp;</h4>
							<p>From 2012 to 2016, Russia <a href="https://csis-prod.s3.amazonaws.com/s3fs-public/publication/170816_Denisentsev_RussiaGlobalArmsMarket_Web.pdf?VHDgCY.h54QWJm1lPCa2w1Lc.BjElJH_" target="_blank">delivered</a> arms to fifty countries. Russia is the world’s second largest arms exporter with a 23 percent market share, behind just the United States (33 percent).</p>
							
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="section history-tool">
			<div class="tool-chapter" id="History">
				<div class="panel-container history tool-chapter--history">
					<div class="chapter chapter-title history" id="history">
						<div class="title-container introduction">
							<div class="icon icon-tools-top"></div>
							<h2>Russia’s Global Ambitions in Perspective</h2>
							<p>Are the Kremlin’s attempts to project influence around the world a fundamentally new element of Russian foreign policy? Yes and no. The Putin regime’s ambitions have deep roots: Russian foreign policy since 2012 fits comfortably in the long-standing historical and intellectual traditions of Soviet and imperial Russian foreign policy. Indeed, continuity with the Soviet era and earlier periods of Russian history is a hallmark of the Kremlin’s current foreign policy. Alluding to this history, the Kremlin legitimizes its pursuits by presenting them as an integral part of Russian identity.</p>
						</div>
					</div>
					<div class="section background text-slide fx--drivers history" id="calloutDrivers">
						<div class="slide slide1b" id="calloutDriversIntro2">
							<div class="callout-text">
									<span class="content-wrap">Current Russian foreign policy reflects three centuries-old drivers of Moscow’s posture on the world stage. </span>
							</div>
						</div>
						<div class="slide">
							<div class="callout-text">
								<span class="content-wrap"><h3>Strategic Depth</h3> Russian security policy has developed in the absence of natural geographic barriers and met the challenge of defending a vast territory through further expansion. Twice in the twentieth century—after the 1917 revolution and the 1991 breakup of the USSR—the Russian state lost large territories and the strategic depth that served as a buffer between it and Europe. Regaining strategic depth has been a major preoccupation of Russian security policy.</span>
							</div>
						</div>
						<div class="slide slide-map1" id="calloutDriversStrategicDepthMap1">
							<div class="callout-text">
								<span class="content-wrap">
									<span>
										<span class="slide-title">Russia in Europe in 1914</span>
										<span class="slide-caption">Source: The Map Archive</span>
									</span>
								</span>
							</div>
						</div>
						<div class="slide slide-map2" id="calloutDriversStrategicDepthMap2">
							<div class="callout-text">
								<span class="content-wrap">
									<span>
										<span class="slide-title">Russia in Europe According to the Brest-Litovsk Treaty (March 1918)</span>
										<span class="slide-caption">Source: The Map Archive</span>
									</span>
								</span>
							</div>
						</div>
						<div class="slide slide-map3" id="calloutDriversStrategicDepthMap3">
							<div class="callout-text">
								<span class="content-wrap">
									<span>
										<span class="slide-title">Russian Federation in 1991</span>
										<span class="slide-caption">Source: The Map Archive</span>
									</span>
								</span>
							</div>
						</div>
						<div class="slide">
							<div class="callout-text">
								<span class="content-wrap"><h3>Recognition as a Great Power</h3>In the eyes of its leaders, Russia’s geographic expanse justifies its claim to great power status. Russia’s vast territory, legacy as an empire and a great power, and history of military conquests are important building blocks of its national identity. Russia’s leaders have been particularly sensitive to any suggestion that Russia does not belong among the major powers.</span>
							</div>
						</div>
						<div class="slide">
							<div class="callout-text">
								<span class="content-wrap"><h3>Uneasy Relationship With the West</h3>Moscow and the West have had complicated relations for centuries. Since the reign of Peter the Great (1682-1725), Russia has defined itself as an integral part of Europe’s political and security fabric, yet has struggled for acceptance as truly European. The theme “in Europe, but not of Europe” has characterized Moscow’s relations with the Continent.</span>
							</div>
						</div>
					</div>
					<div class="section toolkit-history intro">
						<div class="foreground center">
							<div class="callout-text">
								<span class="content-wrap">
									<p>Along with this legacy, Russian foreign policy also inherited a rich toolkit to advance the country’s goals.</p>
									<p>Key elements of this toolkit have been perfected and refined over many decades.</p>
									<p>They have performed reliably for the Russian state at home and abroad.</p>
								</span>
							</div>
						</div>
					</div>
					<div class="section background text-slide fx--toolkit-history" id="slidesToolkitHistory">
						<div class="section" id="slidesToolkitHistoryStick">
							<div class="section slide slide1 politics">
								<div class="callout-text">
									<span class="content-wrap">
										<h3>Ideology as an Instrument of Policy</h3>
										<p>Since the Russian Revolution, the Kremlin has used its ideology of the moment to expand its global reach. In the pre-1991 period, this effort entailed both overt Marxist-Leninist propaganda as well as ideological subversion.</p>
									</span>ß
								</div>
							</div>
							<div class="section slide slide2 politics full-image with-text">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Comintern and Cominform</h3>
											<p>In 1919, in the midst of a civil war, the Bolshevik government established the Communist International (Comintern) to project its political and ideological influence abroad, control foreign Communist parties, and promote revolutionary unrest in capitalist countries. The Comintern (and its successor organization, the Cominform, from Communist Information Bureau) spread propaganda and disinformation, sowed ideological subversion, cultivated political influence, and gathered intelligence abroad. </p>
											<p class="source">Lenin addresses members of the Third International (Comintern) in July 1920 in Petrograd</p>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide3 politics doc">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Eliminating political opponents</h3>
											<div class="image-container">
                                                <div class="docEmbed">
                                                    <embed src="https://carnegieendowment.org/files/SovietUseOfAssassination.pdf" width="100%" height="500" alt="pdf" pluginspage="http://www.adobe.com/products/acrobat/readstep2.html">
												</div>
											</div>
                                            <div class="text">
                                                <div class="content-wrap">
                                                    <span>The young revolutionary state did not shy away from assassinations and executions. In 1924, Soviet intelligence came up with an elaborate plot to lure Boris Savinkov, the leader of an anti-Soviet organization of Russian exiles in France, back to Russia, where he was convicted in a show trial and apparently murdered. Assassination became a hallmark of Soviet intelligence’s handling of defectors and political opponents of the state. The most infamous examples include Ukrainian nationalist leader Stepan Bandera in West Germany in 1959 and Bulgarian dissident Georgi Markov in London in 1978.<div class="slide-caption">A de-classified 1964 <a href="https://catalog.archives.gov/id/7282999">CIA Memorandum</a> prepared for the Warren Commission outlines the Soviet use of assassination and kidnapping.</div></span>
												</div>
											</div>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide3 politics video">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Organizations of influence</h3>
											<div class="image-container">
                                                <div class="videoEmbed">
                                                    <iframe width="560" height="315" src="https://www.youtube.com/embed/ALfDhs-_ce4?start=1108&end=1135&rel=0" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
												</div>
											</div>
                                            <div class="text">
                                                <div class="content-wrap">
                                                    <span>The Soviet Union also relied on a range of front organizations to target hostile émigré groups, penetrate Western governments and societies, and mobilize support for Soviet policies. In addition to the International Department of the Central Committee and the Committee for State Security (KGB), these efforts included various academic, cultural, and commercial organizations, and the Russian Orthodox Church.  <div class="slide-caption">Stanislav Levchenko, a former KGB major, explains the purpose of agents of influence</div></span>
												</div>
											</div>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide5 politics full-image with-text">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Exploiting anticolonialism and leftist movements</h3>
											<p>The demise of colonial empires and the rise of leftist movements in postcolonial Africa created a favorable environment for Moscow to extend its influence. Soviet efforts included providing security, military, and civilian technical assistance; educational opportunities; intelligence training; and ideological indoctrination. </p>
											<p class="source">Soviet advisors in Mozambique (image from <em><a href="https://topwar.ru/106069-voyna-kotoroy-ne-bylo-mozambik.html">Voyennoye Obozreniye</a></em>, from the photo archive of V. Zavadsky)</p>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide1 digital">
								<div class="callout-text">
									<span class="content-wrap">
											<h3>Information and Subversion</h3>
											<p>From the beginning, the Soviet state weaponized information and ideology in its struggle for survival at home and influence abroad.</p>
									</span>
								</div>
							</div>
							<div class="section slide slide2 digital video">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Domestic political consolidation</h3>
											<div class="image-container">
                                                <div class="videoEmbed">
                                                    <iframe width="640" height="480" src="//commons.wikimedia.org/wiki/File:Kinonedelja_No._17_(1918).webm?embedplayer=yes&start=30&end=105" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
												</div>
											</div>
                                            <div class="text">
                                                <div class="content-wrap">
                                                    <span>Soviet leader Vladimir Lenin viewed ideas as key instruments for motivating people and controlling the masses. The Bolsheviks employed then-novel tools, such as print media, mass rallies, news manipulation, and a co-opted creative class (writers, poets, musicians, filmmakers, artists, and so on). Combined with traditional tools of oppression, these instruments enabled the Bolsheviks to impose their vision on the population. <div class="slide-caption">Trains carry agitprop literature to the Russian provinces, captured in a silent newsreel by Soviet filmmaker Dziga Vertov</div></span>
												</div>
											</div>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide3 digital video">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Undermining U.S. foreign relations</h3>
											<div class="image-container">
                                                <div class="videoEmbed">
                                                    <iframe width="560" height="315" src="https://www.youtube.com/embed/ALfDhs-_ce4?rel=0&start=624&end=651" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
												</div>
											</div>
                                            <div class="text">
                                                <div class="content-wrap">
                                                    <span>The Kremlin employed disinformation and forgeries to undermine Western policies. Soviet bloc <a href="https://www.cia.gov/library/readingroom/docs/CIA-RDP84B00049R001303150031-0.pdf">forgeries</a> helped weaken the U.S.-led Middle East peace process in the 1970s. Lawrence Martin-Bittman, a former Czech intelligence officer, recounted: “I have to admit that I am an author of a number of forgeries. . . . The Czechoslovak intelligence service produced at the time a number of forgeries, leaked, for example, to the Egyptian government proving that the American government was planning an assassination against the Egyptian president.”<div class="slide-caption">Former Czech intelligence officer Lawrence Martin-Bittman discusses the effectiveness of forgeries in promoting narratives of American conspiracy.</div></span>
												</div>
											</div>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide4 digital video">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Shaping public opinion in the West</h3>
											<div class="image-container">
                                                <div class="videoEmbed">
                                                    <iframe width="560" height="315" src="https://www.youtube.com/embed/RD2_oN7cvUI" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
												</div>
											</div>
                                            <div class="text">
                                                <div class="content-wrap">
                                                    <span>In the early 1980s, the Kremlin launched a multiyear campaign to turn European public opinion against NATO’s deployment of intermediate-range nuclear missiles to counter Soviet SS-20 missiles. The effort relied on disinformation, fear mongering, and exploiting genuine public concerns about nuclear war.<div class="slide-caption">In June 1981, 15,000 people demonstrate in Bonn against NATO’s decision to station medium-range missiles in West Germany</div></span>
												</div>
											</div>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide1 economic">
								<div class="callout-text">
									<span class="content-wrap">
											<h3>Economic Influence</h3>
											<p>The Soviet Union also leveraged economic assistance to leftist and other friendly governments to expand its web of influence and promote an alternative to the capitalist system. Soviet economic assistance relied on a range of instruments, from economic and technical advisors to loans and sponsorship of industrial development projects.</p>
									</span>
								</div>
							</div>
							<div class="section slide slide2 economic video">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Development assistance</h3>
											<div class="image-container">
                                                <div class="videoEmbed">
                                                    <iframe width="560" height="315" src="https://www.youtube.com/embed/flVCbSIRo0o?start=97&end=125" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
												</div>
											</div>
                                            <div class="text">
                                                <div class="content-wrap">
                                                    <span>For decades, the Soviet Union extended billions in <a href="https://www.cia.gov/library/readingroom/docs/DOC_0000499891.pdf">aid</a> throughout the developing world. A declassified CIA <a href="https://www.cia.gov/library/readingroom/docs/DOC_0000499891.pdf">assessment</a> recognized that Moscow often “succeeded in maximizing the political impact of its comparatively small [aid] effort,” because Soviet assistance emphasized high-visibility, public sector industrial projects. Soviet aid programs also included humanitarian aid, training for military and security forces, and arms transfers.<div class="slide-caption">Egyptian President Gamal Abdel Nasser and Soviet leader Nikita Khrushchev mark the end of the first phase of construction of the Aswan Dam in 1964</div></span>
												</div>
											</div>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide1 military">
								<div class="callout-text">
									<span class="content-wrap">
											<h3>Military Support</h3>
											<p>Military assistance, as well as the use of proxy agents and covert military operations, have been a mainstay of Moscow’s foreign policy—well before the appearance of “little green men” in Ukraine.</p>
									</span>
								</div>
							</div>
							<div class="section slide slide2 military full-image with-text">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Spanish Civil War, 1936–1939</h3>
											<p>The Soviets sent regular military personnel under aliases to advise and fight alongside Republican forces. </p>
											<p class="source">Soviet pilots who participated in air combat in Spain in November 1936</p>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide3 military full-image with-text">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Korean War, 1950<span class="lowercase">s</span></h3>
											<p>The Soviets frequently sent personnel and equipment to crisis zones in Asia and the Middle East. Soviet military pilots secretly flew missions during the Korean War and manned North Vietnamese anti-aircraft batteries.</p>
											<p class="source">Soviet instructors and North Vietnamese crewmen at an anti-aircraft training center in Vietnam in 1965</p>
										</span>
									</span>
								</div>
							</div>
							<div class="section slide slide4 military doc">
								<div class="callout-text">
									<span class="content-wrap">
										<span>
											<h3>Egypt-Israel War of Attrition, 1970</h3>
											<div class="image-container">
                                                <div class="docEmbed">
                                                    <embed src="https://carnegieendowment.org/files/CIA-DOC_0000272978.pdf#page=3" width="100%" height="500" alt="pdf" pluginspage="http://www.adobe.com/products/acrobat/readstep2.html">
												</div>
											</div>
                                            <div class="text">
                                                <div class="content-wrap">
                                                    <span>Moscow secretly deployed Soviet fighter jets and air defense units to Egypt during the so-called War of Attrition with Israel. <div class="slide-caption">A contemporaneous <a href="https://www.cia.gov/library/readingroom/docs/DOC_0000272978.pdf">CIA report</a> from May 1970 assesses Soviet deployments in Egypt and its involvement in the Egypt-Israeli conflict</div></span>
												</div>
											</div>
										</span>
									</span>
								</div>
							</div>
							<div class="section toolkit-history conclusion">
								<div class="foreground center">
									<p>Following the Soviet Union’s collapse in 1991, Russia largely retreated from the world stage. Once Russia recovered a measure of its economic strength, political stability, and military muscle a decade later, it did not take long for it to reclaim a significant part of its foreign policy legacy. Russian foreign policy today lacks the rigid ideological guidelines and vast resources of the Soviet period, but it reflects greater flexibility, adaptability, and opportunism. There is every indication that both Moscow’s ambitions and its toolkit will endure, even in a post-Putin era.<a href="https://carnegieendowment.org/2019/02/20/russia-s-global-ambitions-in-perspective-pub-78067" class="main-cta center">Go deeper<strong>Read the report</strong></a></p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="tool-chapter" id="LatinAmerica">
			<div class="panel-container latin-america">
				<div class="chapter chapter-title latin-america" id="parallaxLatinAmericaContainer">
					<div class="title-container">
						<strong>Regional Deep Dive</strong>
						<h2>Latin America</h2>
						<p>For largely geopolitical reasons, Moscow is expanding its presence in Latin America at Washington’s expense despite Russia’s lack of geographic proximity and limited resources. </p>
						<p>The Kremlin is taking advantage of the deteriorating relationships between the United States and many of its southern neighbors. Russia supports anti-American populist candidates in elections throughout Latin America and is trying to expand trade and investment opportunities with the region.</p>
						<p>The Kremlin likely calculates that closer relations with Latin America&mdash;whether built on arms sales, trade, energy deals, or political congruity&mdash;will complicate U.S. foreign policy. If these overtures help Russian military and security actors gain access to the United States’ backyard, so much the better.</p>
					</div>
				</div>
			</div>
		</div>
		<div class="section map map_in-focus map-la" id="latin-americaStartSection">
			<div class="foreground">
				<h3 id="latin-americaStart">Moscow in Latin America</h3>
			</div>
		</div>
		<div id="latin-americaStick">
			<div class="slide latin-america-slideEnd latin-america b-lazy b-loaded slide--active" id="energySlide6"
				style="background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-latam-end.gif); opacity: 1;">
			</div>
		</div>
		<div class="section latin-america latAm-captions">
			<div class="caption-list foreground">
				<h4>Venezuela</h4>
				<p>Rosneft has stakes in five major oil projects and purchased two new offshore gas blocks in
					2017. Venezuela owes Rosneft $6 billion.</p>
				<p class="source">Source: <a
						href="https://www.bloomberg.com/news/articles/2017-11-03/venezuela-restructuring-heightens-rosneft-s-6-billion-risk"
						target="_blank">Bloomberg</a></p>
				<div class="caption-secondary">
					<p class="political">
						<strong>Political Influence</strong> Maduro has followed in the late Hugo Chávez’s footsteps, prioritizing close ties with Russia. Over the past year, Moscow has stepped in to provide a crucial political and financial lifeline to Maduro’s government.
					</p>
					<p class="economic">
						<strong>Economic Influence</strong> A Russian debt relief deal in late 2017 gave Caracas additional breathing room. As foreign companies have retreated from the slow collapse of PDVSA, Venezeula’s state oil company, Rosneft has provided billions of dollars in <a href="https://www.nytimes.com/2017/10/29/business/energy-environment/russia-venezula-oil-rosneft.html">prepayments for oil deliveries</a> to keep the company afloat. In return, Rosneft has gained preferential access to Venezuela’s oil and gas sector.
					</p>
					<p class="military">
						<strong>Military Ties</strong> Russia was a leading supplier of arms to Venezuela in the 2000s. Venezuela has also hosted Russian nuclear-capable bombers and a Russian naval flotilla in 2008, as part of Moscow’s efforts to signal to Washington that it can deploy forces in the U.S.’s backyard.
					</p>
				</div>
				<h4>Cuba</h4>
				<p>Cuba was Russia’s primary partner in the region during the Cold War. The abrupt end of Soviet economic subsidies disrupted the Cuban economy in the early 1990s. Cuba’s new leader, Miguel Díaz-Canel, now faces the challenge of governing and rejuvenating the economy without the revolutionary credentials of Fidel or Raúl Castro. Cuba’s economic travails are compounded by a decline in subsidized energy imports from Venezuela and a freeze on the normalization of U.S.-Cuba commercial ties imposed by the administration of U.S. President Donald Trump.</p>
				<div class="caption-secondary">
					<p class="political">
						<strong>Political Influence</strong> Russian outreach efforts in Latin America have focused prominently on Havana due to its strategic location. Both Medvedev and Putin have visited Cuba during their tenures as president. Cuba has also <a href="https://www.reuters.com/article/us-ukraine-crisis-un/u-n-general-assembly-declares-crimea-secession-vote-invalid-idUSBREA2Q1GA20140327">supported Russia</a> in international forums such as the UN General Assembly.
					</p>
					<p class="economic">
						<strong>Economic Influence</strong> Russia has provided Cuba with generous <a href="https://www.wsj.com/articles/russia-writes-off-cuba-debt-1405083869">debt relief</a>, and Rosneft began oil shipments to Cuba last year to help compensate for the drop in Venezuelan exports. The two countries are ramping up projects in the energy and railway sectors.
					</p>
					<p class="military">
						<strong>Military Ties</strong> Moscow has helped finance and support modernization of Cuba’s defense sector. Russian officials have periodically floated the possibility of reestablishing the Russian signals <a href="https://www.nytimes.com/2016/10/08/world/europe/russia-seeks-to-reopen-military-bases-in-vietnam-and-cuba.html">intelligence collection facility at Lourdes</a>.
					</p>
				</div>
				<h4>Nicaragua</h4>
				<p>Nicaragua’s relations with Russia have flourished following Sandinista leader Daniel Ortega’s return to power in 2007. Nicaragua has been in crisis since April 2018 when anti-government protests erupted after Ortega’s surprise decision to slash welfare benefits. Thousands of protestors have taken to the streets demanding Ortega’s resignation, and the state’s violent crackdown has left over 170 people dead.</p>
				<div class="caption-secondary">
					<p class="political">
						<strong>Political Influence</strong> Nicaragua is one of Russia’s most steadfast political and military partners in the region. Managua has supported Moscow diplomatically on issues such as the Crimean annexation, the war in eastern Ukraine, and <a href="https://www.nytimes.com/2008/09/04/world/americas/04iht-georgia.4.15904253.html">recognition of Abkhazia and South Ossetia</a>. For its part, the Russian MFA has harshly <a href="http://tass.ru/mezhdunarodnaya-panorama/5159381">condemned</a> foreign powers for allegedly meddling in Nicaraguan domestic politics.
					</p>
					<p class="military">
						<strong>Military Ties</strong> Russia has supplied Nicaragua with arms, including fifty new <a href="https://apnews.com/e7ac9d8e16974d0b8321b438a3541033">T-72 tanks</a>. Moscow also has constructed a GLONASS <a href="https://www.washingtonpost.com/world/the_americas/the-soviet-union-fought-the-cold-war-in-nicaragua-now-putins-russia-is-back/2017/04/08/b43039b0-0d8b-11e7-aa57-2ca1b05c41b8_story.html?utm_term=.b26f79cdd4d0">satellite communications facility</a> in Managua and an anti–drug trafficking center, facilities that are believed to be used for intelligence gathering. In 2015, the Nicaraguan parliament voted to allow Russian warships to dock in Nicaraguan ports.
					</p>
				</div>
				<h4>Brazil</h4>
				<p>Russia has cultivated relations with Brazil via the BRICS, a grouping that includes Brazil, China, India, Russia, and South Africa. Brazil’s economy is the largest in South America, but political and economic ties with Moscow remain fairly limited. Buffeted by a series of corruption scandals, Brazil will hold a presidential election in October 2018. The contest is essentially wide open.</p>
				<div class="caption-secondary">
					<p class="economic">
						<strong>Economic Influence</strong> Russia-Brazil <a href="http://www.brazilgovnews.gov.br/news/2017/06/brazil-russia-trade-reaches-us-4-3-billion-a-year">bilateral trade</a> rose to $4.3 billion in 2016, and Brazil is <a href="https://comtrade.un.org/data/">Russia’s largest trade partner</a> in Latin America. Russia imports food and agricultural products from Brazil and exports fertilizer, mineral fuels, and metals. In 2016, Russia purchased 90 percent of its <a href="https://gain.fas.usda.gov/Recent%20GAIN%20Publications/Livestock%20and%20Products%20Annual_Moscow_Russian%20Federation_11-28-2017.pdf">imported pork</a> from Brazil, but Moscow has since begun tightening market access to protect domestic producers.
					</p>
					<p class="info">
						<strong>Information Space</strong> Brazilian citizens are extremely active online. The government is increasingly worried about the <a href="https://www.nytimes.com/2018/02/17/world/americas/brazil-election-fake-news.html">impact of fake news</a> sponsored by rival political parties and, to a lesser extent, overseas actors on its upcoming presidential election, but has not explicitly expressed fear of Russian interference. A <a href="http://dapp.fgv.br/en/robots-social-networks-politics-fgv-dapp-study-points-illegitimate-interference-public-debate-web/">2017 study</a> by the Brazilian think tank FGV-DAPP on social media interference in Brazil’s 2014 election uncovered a significant network of bots and fake profiles on Twitter with Russian markers.
					</p>
				</div>
				<h4>Mexico</h4>
				<p>Russia has taken some steps to capitalize on the sharp disagreements over trade and immigration that have roiled U.S.-Mexican relations under President Trump. Ties are likely to be strained further if Andrés Manuel López Obrador, the populist former mayor of Mexico City, wins the country’s July 2018 presidential election. Lopez Obrador has tried to use humor to rebut claims that Moscow and Russian state propagandists favor his candidacy.</p>
				<div class="caption-secondary">
					<p class="economic">
						<strong>Economic Influence</strong> After Brazil, Mexico is Russia’s <a href="https://comtrade.un.org/data/">largest economic partner</a> in the region, although bilateral trade remains relatively small in dollar terms. As U.S.-Mexico relations have soured, some Russian and Mexican government officials have made <a href="http://www.eluniversal.com.mx/english/pena-and-putin-acknowledge-investment-opportunities">public remarks</a> about the value of deeper commercial ties. These comments stand in contrast to Mexico’s more cautious stance on China.
					</p>
					<p class="political">
						<strong>Political Influence</strong> López Obrador appears to be the Kremlin’s favored Mexican presidential candidate due to his anti-American views and the possibility that his victory would cause significant heartburn for the Trump administration. Senior U.S. officials— including former national security adviser <a href="http://thehill.com/homenews/administration/367844-mcmaster-points-to-russian-interference-in-mexican-election">H.R. McMaster</a> and former secretary of state <a href="https://www.ft.com/content/09df2f96-0855-11e8-9650-9c0ad2d7c5b5">Rex Tillerson</a> hinted publicly about possible Russian interference in the election.
					</p>
					<p class="info">
						<strong>Information Space</strong> Moscow has expanded Spanish-language television programming and social media accounts aimed at Mexico and other Latin American countries. A Spanish affiliate of <em>RT</em> typically embraces strong anti-American themes.
					</p>
				</div>
				<h4>Bolivia</h4>
				<p>Bolivia’s staunchly anti-U.S. stance under President Evo Morales has made it an attractive political partner for Moscow. Of additional interest to Moscow given its expertise in natural resource extraction and delivery, Bolivia boasts the <a href="http://www.bbc.com/news/world-latin-america-18727507">second-largest</a> natural gas reserves in South America.</p>
				<div class="caption-secondary">
					<p class="political">
						<strong>Political Influence</strong> United by their vocal opposition to the U.S.-led international order, Bolivia and Russia have cooperated closely at the United Nations (UN). Bolivia was one of eleven countries that voted against a <a href="https://www.washingtonpost.com/news/worldviews/wp/2014/03/27/here-are-the-11-u-n-members-that-voted-against-a-resolution-on-ukraines-unity/?utm_term=.0269cbb50444">March 2014 resolution</a> in the UN General Assembly condemning Russian actions in Crimea. In April 2018, La Paz and Moscow teamed up to oppose a resolution criticizing Syria’s use of chemical weapons in Douma.
					</p>
					<p class="economic">
						<strong>Economic Influence</strong> Gazprom has been working on a variety of oil- and gas-related projects in Bolivia in recent years. Similarly, Rosatom signed a contract build a <a href="http://www.rosatom.ru/en/press-centre/news/rosatom-signs-the-contract-for-nuclear-technology-center-construction-in-bolivia/">nuclear research reactor</a> in the Bolivian city of El Alto in September 2017.
					</p>
					<p class="military">
						<strong>Information Space</strong> Russia and Bolivia signed a <a href="http://tass.com/economy/960612">defense cooperation agreement</a> in August 2017 and are discussing potential arms deals as part of <a href="http://www.janes.com/article/73413/russia-bolivia-boost-defence-co-operation">Bolivia’s military recapitalization program</a>.
					</p>
				</div>
				<h4>Argentina</h4>
				<p>Russia’s ties with Argentina flourished under populist former president Cristina Fernández de Kirchner from 2007 to 2015. Kirchner provided tacit political support for Moscow’s illegal annexation of Crimea, while she pursued cooperation with Russia on nuclear power and other energy projects. Rather curiously, relations were also strong during the late Soviet period, thanks to the <a href="https://www.nytimes.com/1981/07/12/weekinreview/argentina-and-soviet-are-no-longer-just-business-partners.html">dependence</a> of Argentina’s anti-Communist, junta-led dictatorship on Soviet exports of beef and grain. Kirchner’s successor, former businessman Mauricio Macri, took office in 2015 and was expected to put relations with Russia on the back burner.</p>
				<div class="caption-secondary">
					<p class="political">
						<strong>Political Influence</strong> Macri confounded expectations that he would downplay relations with Moscow when he formally reaffirmed the <a href="http://valdaiclub.com/a/highlights/russia-argentina-will-the-strategic-partnership/">Russia-Argentina strategic partnership</a> during a visit to Moscow in January 2018. Russian President Vladimir Putin plans to attend the G20 summit in Buenos Aires in the autumn of 2018.
					</p>
					<p class="economic">
						<strong>Economic Influence</strong> Trade and commercial cooperation is decidedly modest in scope. Argentina has had limited success in trying to capitalize on Russia’s countersanctions against Western food imports. Joint nuclear energy projects remain stalled.
					</p>
				</div>
				<h4>Colombia</h4>
				<p>Colombia has exceptionally strong ties to the United States, but is surrounded by Venezuela and other ALBA states with close ties to Russia. Colombia’s 2018 two-round presidential election in May and June 2018 is the first contest since the 2016 peace deal that ended the long-running insurgency led by the Revolutionary Armed Forces of Colombia (FARC). The prevalent use of <a href="https://translate.google.com/translate?sl=es&tl=en&js=y&prev=_t&hl=en&ie=UTF-8&u=http%3A%2F%2Fwww.eltiempo.com%2Fmundo%2Feeuu-y-canada%2Ffrank-mora-afirma-que-rusia-interfiere-en-elecciones-colombianas-2018-173594&edit-text=&act=url">mobile devices and social media</a> makes Colombia potentially susceptible to efforts to manipulate public opinion.</p>
				<div class="caption-secondary">
					<p class="political">
						<strong>Political Influence</strong> Given Colombia’s close alignment with the United States, high-level political ties with Russian leaders have been minimal. During the height of the FARC insurgency, a network of Russian criminal groups and corrupt military figures <a href="http://www.nbcnews.com/id/3340035/#.WwgVDlMvzOQ">supplied the guerillas</a> with weapons in exchange for cocaine. The extent of Russian government involvement in these efforts, which began in the early 1990s, remains fuzzy. 
					</p>
					<p class="info">
						<strong>Economic Influence</strong> Amid heightened tensions with Venezuela, Colombia’s defense minister in March 2018 <a href="https://www.voanews.com/a/colombia-voter-registration-cyberattacks-russia-allies/4300571.html">accused Caracas</a> of a series of cyberattacks on the country’s voter registration system. Colombian defense sources have hinted that Russia might be connected to these efforts. Meanwhile, Russian media outlets, including <em>RT,</em> lent support to <a href="http://securingdemocracy.gmfus.org/blog/2018/01/04/russian-influence-mexican-and-colombian-elections">ex-FARC leader Rodrigo Londoño’s</a> short-lived presidential candidacy in early 2018. (Londoño ultimately <a href="https://www.cnn.com/2018/03/08/americas/colombia-farc-leader-presidential-bid/index.html">dropped out of the race</a> prematurely due to health problems.)
					</p>
				</div>
				<p>Russia’s geopolitical aim in Latin America is to counter perceived U.S. encroachment in Moscow’s own backyard and boost Russia’s credentials as a global power.</p>
				<p>Russian efforts have focused on deepening partnerships to further Moscow’s desire for a multipolar international system and to cultivate an anti-U.S. constituency in the region, while also exploiting developments to disrupt U.S. relations with key regional partners.</p>
				<p>For the United States, Russia’s increasing presence in the Western Hemisphere is an unwelcome development that could further aggravate relations with its neighbors and present new threats.</p>
				<p class="main-cta">Go deeper:<a href="https://carnegieendowment.org/2018/05/03/russia-playing-geopolitical-game-in-latin-america-pub-76228">Read the report</a></p>
			</div>
		</div>
		<div class="project-questions">
			<div class="conclusion-questions callout callout-first">
				<div class="callout-inside">
					<div class="callout-text">
						<div class="content-wrap">
							Russia has excelled at inserting itself into complex situations and making sure its interests are protected.
						</div>
						<div class="content-wrap">
							Russia’s global forays do not face significant economic constraints, given Moscow’s frequent reliance on low-cost methods to project power and influence.
						</div>
						<div class="content-wrap">
							The Kremlin has a knack for seizing opportunities and exploiting other actors’ weaknesses to further its aims. Russia’s toolkit, especially its exploitation of information and propaganda tools, will continue to evolve and embrace new and emerging technologies.     
						</div>
						<div class="content-wrap">
							Going forward, Putin’s more assertive foreign policy will be a factor—and potentially a spoiler—for Western policymakers in a growing number of regions.    
						</div>
						<div class="section cta">
							<a class="button" href="/specialprojects/thereturnofglobalrussia/"><em>Explore the research behind</em> the Return of Global Russia</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script setup>
import FeatureFragment from '@fragments/pages/FeatureFragment.ts';

const pageData = await usePageData(FeatureFragment, 'features');
</script>
<style lang="scss" scoped>
//-layout
$gutter: 30px;
$site-width: 1000px;

$blue-light: #3d71b7;
$blue-main: #092e55;
$gray-light: #e4e3dd;
$gold: #a28556;
$black: #000;
$white: #fff;
$teal: #00a79d;
$dark-gray-new: #6e6e71;
$light-gray: #f5f5f5;
$dark-gray: #929497;
$yellow: #fbb040;
$primary-light-blue: #3183b1;
$primary-dark-blue: #072a42;
$teal-light: #99dcd8; // 40% $teal
$primary-dark-blue-light: #9caab3; // 40% $primary-dark-blue
$yellow-light: #fee7c5; //40% $yellow

$primary-dark: #59253a;
$primary-dark-duo: #190b10;
$primary-mid: #78244c;
$primary-mid-scrn: #decfd3;
$primary-light: #895061;
$primary-light-scrn: #c19bad;

$proj-prime: #3B1F2B;
$proj-comp: #C73E1D;
$proj-comp-dark: #93280e;
$proj-comp-alt: #2E86AB;
$proj-accent: #F18F01;

$econ-primary-med: #8E00B7;
$pol-primary-med: #AC064C;
$info-primary-med: #B82300;
$mil-primary-med: #AC5B0C;

$in-focus-prime: #A23B72;

$comp-primary: $blue-main;
$comp-secondary: $blue-light;

$roman-normal-book: 'Frutiger Neue LT W06 Book', Arial, sans-serif;
$roman-italic-book: 'Frutiger Neue LT W06 Book It', Arial, sans-serif;
$roman-normal-bold: 'Frutiger Neue LT W06 Bold', 'Arial Black', Gadget, sans-serif;
$roman-italic-bold: "Frutiger Neue LT W06 Bold It", "Arial Black", Gadget, sans-serif;


//layout basics
.foreground {
	position: relative;
	margin: 0 auto;
	box-sizing: content-box;
	max-width: $site-width;
	padding-left: calc($gutter / 2);
	padding-right: calc($gutter / 2);
}

.section {
	display: block;
	clear: both;
	margin-top: $gutter;
	margin-bottom: $gutter;
}

.cols {
	clear: both;
	//use a .no-gutters class to undo these default styles
	margin-left: calc($gutter / 2) * -1;
	margin-right: calc($gutter / 2) * -1;

	&:before,
	&:after {
		content: ' ';
		display: table;
		height: 0;
		clear: both;
		font-size: 1px;
		line-height: 0px;
		visibility: hidden;
	}
}

.cols--equal-heights {
	flex-wrap: wrap;

	&,
	>.col {
		display: flex;
	}

	>.col {
		flex-direction: column;
	}

	.banded {
		flex: 1 0 auto;
	}
}

.cols {
	clear: both;
}

.col {
	float: left;

	// @include bp(mobile) {
	// float: none;
	// width: 100% !important;

	// > .gutters {
	// margin-left: 0;
	// margin-right: 0;
	// }
	// }
}

.col-80 {
	width: 80%;
}

.col-70 {
	width: 70%;
}

//two across
.col-50 {
	width: 50%;
}

.col-30 {
	width: 30%;
}

.col-33 {
	width: 33.33%;
}

//four across
.col-25 {
	width: 25%;
}

//five across
.col-20 {
	width: 20%;
}

.padded {
	padding: $gutter;
}

.nav-tabs {
	div {
		border: 0 !important;
		text-transform: uppercase;
	}

	>li {
		margin-bottom: 0;
		float: left;
		position: relative;
		display: block;

		>div {
			line-height: 1;
			border-radius: 0;
		}
	}
}

.gutter-right {
	margin-right: 30px;
}

.gutter-left {
	margin-left: 30px;
}

p {
	margin-bottom: 10px;
	font-size: 2em;
	line-height: 1.5em;
}

body {
	background-color: $white;
	color: $black;
	font-family: $roman-normal-book;

	.spacer {
		text-align: center;
		min-height: 100px;
	}

	.background-photo {
		background-repeat: no-repeat;
		background-position: center center;
		background-size: cover;
		height: 100vh;
		width: 100vw;

		h1 {
			background-color: rgba(255, 255, 255, 0.6);
			color: $proj-accent;
			display: inline-block;
			font-family: $roman-normal-bold;
			font-size: 8rem;
			line-height: 0.9em;
			margin: 30px 25%;
			padding: calc($gutter * 2) calc($gutter * 2) calc($gutter * 1.5);
			text-align: center;
			text-shadow: 2px 2px 5px rgba(255, 255, 255, 1);
			text-transform: uppercase;
			width: auto;

			>span {
				color: $proj-comp;
				display: block;
				font-family: $roman-italic-bold;
				font-style: italic;
				font-size: 2rem;
				line-height: 1.8em;
				margin-top: -0.6em;
				text-transform: none;
			}
		}

		&.sticky {
			z-index: 2;
		}

		&.first {
			background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/section-slideIntro.jpg);
			background-color: $proj-comp-alt;
			background-blend-mode: luminosity;
		}

		.putin {
			background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/intro-putin-iso.png);
			background-position: center center;
			background-repeat: no-repeat;
			background-size: contain;
			height: 600px;
			z-index: 8;
			position: absolute;
			width: 400px;
			display: inline-block;
			opacity: 1;
			bottom: -30px;
		}
	}

	.expanding {
		padding: calc(calc($gutter * 7)) 0 calc(calc($gutter * 8)) 0;

		.icon {
			background-size: 100% auto;
			height: 300px;
			margin: 0 auto;
			width: 300px;

			&.icon-tools-top {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-tools-intro1.png);
				margin-bottom: calc(calc($gutter * 4));
			}
		}

		p {
			color: $proj-comp-alt;
			font-family: $roman-normal-book;
			font-size: 2em;
			line-height: 2em;
			// margin-right: 9em;
		}
	}

	h3 {
		color: $primary-light;
	}

	.footer {
		padding: calc($gutter * 2) 0;
		position: relative;
		z-index: 99;

		.section {
			display: block;
			margin-bottom: $gutter;
			margin-top: $gutter;
		}

		.footer-logo {
			display: inline-block;
		}
	}

	.popcorns,
	.fx--on-the-march,
	.fx--drivers {
		background-color: $proj-comp;
		color: $white;
		margin-bottom: 0;

		.callout {
			text-align: left;

			&.callout-first {
				background-color: $proj-comp;
				color: $white;
				font-family: $roman-normal-bold;
				font-size: 3.5rem;
				height: 100vh;
				width: 100%;
				position: relative;
				text-align: center;
				width: 100%;
				z-index: 1;

				.callout-text {
					display: table;
					height: 100vh;
					margin: 0 auto 0;
					width: 70%;
					z-index: 99;

					>.content-wrap {
						display: table-cell;
						vertical-align: middle;
					}
				}

				em {
					font-family: $roman-normal-bold;
					font-style: normal;
				}
			}

			&.callout-second {
				background-color: $proj-comp;
				color: $white;
				font-size: 4rem;
				position: relative;
				text-align: center;
				width: 100%;
				z-index: 1;

				.callout {
					font-family: $roman-normal-bold;
					font-size: 3.5rem;
					height: 100vh;
					width: 100%;

					.callout-text {
						display: table;
						height: 100vh;
						margin: 0 auto 0;
						width: 70%;
						z-index: 99;

						>.content-wrap {
							display: table-cell;
							vertical-align: middle;
						}
					}

					em {
						font-family: $roman-normal-bold;
						font-style: normal;
					}
				}

				.colls {
					margin-left: 0;
					margin-right: 0;
				}
			}

			&.callout-third {
				background-color: $proj-comp;
				font-size: 3rem;
				height: 100vh;
				position: relative;
				text-align: center;
				width: 100%;
				z-index: 1;
				margin-bottom: 0;

				.callout {
					font-family: $roman-normal-bold;
					font-size: 3.5rem;
					display: table;
					height: 100vh;
					margin: 0 auto 0;
					width: 70%;
					z-index: 99;

					>.content-wrap {
						display: table-cell;
						vertical-align: middle;
					}

					em {
						font-family: $roman-normal-bold;
						font-style: normal;
					}
				}
			}
		}

		.popcorn-pieces {
			clear: both;
			display: block;

			&.center {
				margin: 0 auto;
				position: relative;
			}

			>div {
				background-position: center center;
				background-size: auto 100%;
				color: $white;
				font-family: $roman-normal-bold;
				font-size: 0.6em;
				height: 400px;
				line-height: 1.1em;
				text-align: center;
			}

			&.start {
				margin-left: 0;
				margin-right: 0;
				width: 100%;

				.pops {
					font-size: 2rem;
					height: 100vh;

					>.content-wrap {
						text-shadow: none;

						.container {
							background-color: $proj-accent;
							display: inline-block;
							line-height: 1.3em;
							padding: $gutter;

							>em {
								display: block;
								font-size: 2.6rem;
								text-transform: uppercase;
							}
						}
					}
				}
			}

			&.top {
				.pops {
					>.content-wrap {
						text-shadow: none;

						.container {
							background-color: $proj-accent;
							display: inline-block;
							font-size: 1.5rem;
							line-height: 1em;
							padding: calc($gutter / 2);
							width: 75%;

							em {
								font-family: $roman-italic-bold;
							}
						}
					}
				}
			}

			&.circles {
				>div {
					background-color: $primary-mid;
					border-radius: 50%;
					height: 150px;
					width: 150px;
				}

				&.start {
					.pops {
						background-color: $primary-light;
						height: 200px;
						margin: calc($gutter / 2);
						width: 200px;
						-webkit-box-shadow: 2px 4px 18px 0 #aaa;
						box-shadow: 2px 4px 18px 0 #aaa;
					}
				}
			}
		}

		.pops {
			background-blend-mode: luminosity;
			background-color: $proj-comp-alt;
			float: left;
			padding: 0.5em;
			position: relative;
			display: table;

			>.content-wrap {
				display: table-cell;
				text-shadow: 2px 2px 5px $black;
				vertical-align: middle;
			}

			em {
				font-family: $roman-italic-bold;
				font-variant: none;
			}
		}

		&.energy,
		&.arms-sales {
			padding-bottom: 40vh;

			.background {
				background-color: $proj-comp;

				display: block;
				height: 100vh;
				// opacity: 0.1;
				width: 100%;
			}

			h3 {
				text-align: center;
				color: $proj-accent;
				font-family: $roman-normal-bold;
				font-size: 2.5rem;
				line-height: 1.1em;
				margin: $gutter 5% 0;
				text-align: center;
				text-transform: uppercase;
			}

			.callout-first {
				font-size: 3.5rem;
			}

			.popcorn-pieces {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/world-map.png);
				background-position: center top;
				background-repeat: no-repeat;
				background-size: 100% auto;
				display: block;
				height: 100%;
				position: relative;

				.pops {
					background-color: transparent;
					background-repeat: no-repeat;
					background-size: 60% auto;
					position: absolute;

					em {
						color: $white;
						display: inline-block;
						font-family: $roman-normal-bold;
						font-size: 1rem;
						line-height: normal;
						padding: 2px 5px 4px;
						text-shadow: none;
						text-transform: uppercase;
					}
				}
			}
		}

		&.energy {
			#calloutEnergy {
				padding-top: 3%;
			}

			h3 {
				padding-top: 0;
			}

			.popcorn-pieces .pops {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/energy-map--rosatom-logo.png);	
			}
		}

		&.arms-sales {
			#calloutArms {
				padding-top: 5%;
			}

			.popcorn-pieces .pops {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/arms-map--fighter-icon.png);
			}
		}

		&.history.text-slide {
			color: $white;
			font-family: $roman-normal-bold;
			line-height: 1.6em;

			.slide {
				background-color: $proj-comp;
				background-position: center bottom;
				background-size: cover;
				height: 100vh;

				.content-wrap span {
					background-color: rgba(0, 0, 0, 0.5);
					display: block;
					padding: calc($gutter * 1.5);

					h3 {
						margin-top: 0;
					}

					.slide-title {
						background-color: transparent;
						font-size: 2.5rem;
						line-height: 1.1em;
						padding: 0;
					}

					.slide-caption {
						background-color: transparent;
						font-family: $roman-italic-book;
						font-size: 1.5rem;
						margin-top: $gutter;
						padding: 0;	
					}
				}
			}

			.callout-text {
				display: table;
				height: 100vh;
				margin: 0 auto 0;
				width: 70%;
				z-index: 99;

				>.content-wrap {
					display: table-cell;
					font-size: 3rem;
					line-height: normal;
					vertical-align: middle;

					h3 {margin-bottom: 10px; font-size: 3.2rem;}
				}
			}

			.slide2,
			.slide3,
			.slide4 {
				.callout-text .content-wrap {
					font-size: 2.2rem;

					h3 {
						font-size: 3.5rem;
						margin-bottom: $gutter;
					}
				}
			}

			.slide1 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-history_intro.png);
			}

			.slide1b {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-history_intro.png);
			}

			.slide2 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-history_intro.png);
			}

			.slide3 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-history_outro.png);
			}

			.slide4 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-history_outro.png);
			}

			.slide5 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-history_outro.png);
			}

			.slide-map1,
			.slide-map2,
			.slide-map3 {
				.callout-text {
					width: 90%;

					.content-wrap {
						display: inline-block;
						margin-top: 50vh;
						max-width: 40%;
					}
				}
			}

			.slide-map1 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-history1.png);
			}

			.slide-map2 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-history2.png);
			}

			.slide-map3 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-history3.png);
			}
		}
	}
}

// highlighter
:deep(.highlighting) {
	position: relative;
	display: inline-block;

	&:after {
		content: "";
		background: #F18F01;
		height: 100%;
		width: 0%;
		position: absolute;
		z-index: -1;
		transition: all 1s ease 0s;
		left: 0;
	}

	&.lit:after {
		width: 100%;
	}
}

.tools {
	background: $proj-prime;
	padding-bottom: calc($gutter * 4);
	position: relative;
	margin-top: 0;
	z-index: 99;

	>.foreground {
		padding: calc($gutter * 2) 0 $gutter 0;
	}

	p,
	ul {
		color: $white;
		font-size: 1.5em;
	}

	h2 {
		background: $proj-prime;
		color: $proj-accent;
		font-family: $roman-normal-bold;
		font-size: 4.5em;
		line-height: 0.85em;
		padding: 1em 0 0 0;
		text-align: center;
		text-transform: uppercase;
		z-index: 99;
	}

	.cols {
		padding-top: calc($gutter * 2);

		.col {
			background-position: center top;
			background-repeat: no-repeat;
			background-size: 70% auto;
			color: $proj-accent;
			font-family: $roman-normal-bold;
			font-size: 2rem;
			line-height: 1em;
			padding-top: 210px;
			text-align: center;
			text-transform: uppercase;

			&.economic {
				background-image: url(http://carnegieendowment.org/layout-images/interactives/global-russia/icon-toolnav-economic.png);
			}

			&.political {
				background-image: url(http://carnegieendowment.org/layout-images/interactives/global-russia/icon-toolnav-political.png);
			}

			&.information {
				background-image: url(http://carnegieendowment.org/layout-images/interactives/global-russia/icon-toolnav-information.png);
			}

			&.military {
				background-image: url(http://carnegieendowment.org/layout-images/interactives/global-russia/icon-toolnav-military.png);
			}

			a {
				color: $proj-accent;
				cursor: pointer;
				display: block;
				font-size: 3rem;
				line-height: 3rem;
				width: 90%;
				margin: 0 auto;
			}
		}
	}
}

.expanding {
	padding: calc($gutter * 7) 0 calc($gutter * 8) 0;

	.icon {
		background-size: 100% auto;
		height: 300px;
		margin: 0 auto;
		width: 300px;

		&.icon-tools-top {
			background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-tools-intro1.png);
			margin-bottom: calc($gutter * 4);
		}
	}

	p {
		color: $proj-comp-alt;
		font-family: $roman-normal-book;
		font-size: 2em;
		line-height: 2em;
	}
}

#EconomicTool.tool-chapter .panel-container .conclusion {
	.icon-chapter-top {
		background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-chaptertop-economic.png);
		background-size: 100% auto;
		height: 300px;
		margin: 0 auto 120px auto;
		width: 300px;
	}
}

#PoliticalTool.tool-chapter .panel-container .conclusion {
	.icon-chapter-top {
		background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-chaptertop-political.png);
		background-size: 100% auto;
		height: 300px;
		margin: 0 auto 120px auto;
		width: 300px;
	}
}

#DigitalTool.tool-chapter .panel-container .conclusion {
	.icon-chapter-top {
		background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-chaptertop-info.png);
		background-size: 100% auto;
		height: 300px;
		margin: 0 auto 120px auto;
		width: 300px;
	}
}

#MilitaryTool.tool-chapter .panel-container .conclusion {
	.icon-chapter-top {
		background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-chaptertop-military.png);
		background-size: 100% auto;
		height: 300px;
		margin: 0 auto 120px auto;
		width: 300px;
	}
}

#conclusion.conclusion {
	.icon-chapter-top {
		background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-tools-intro1.png);
	}
}

// chapters
.tool-chapter {
	height: 100%;
	width: 100vw;
	z-index: 99;
	background: -webkit-linear-gradient(bottom, white, transparent);
	/* For Safari 5.1 to 6.0 */
	background: -o-linear-gradient(bottom, white, transparent);
	/* For Opera 11.1 to 12.0 */
	background: -moz-linear-gradient(bottom, white, transparent);
	/* For Firefox 3.6 to 15 */
	background: linear-gradient(to top, white, transparent);
	/* Standard syntax (must be last) */

	.panel-container {
		background: $white;
		// border-top: 10px solid $proj-accent;
		-webkit-box-shadow: 0 -10px 20px -10px #000000;
		box-shadow: 0 -10px 20px -10px #000000;
		display: block;
		position: relative;
		width: 100%;
		z-index: 99;

		.section {
			font-size: 1.3rem;
			margin-bottom: calc($gutter * 2);

			h3 {
				font-size: 3rem;
			}

			.contentImg {
				margin-left: -calc($gutter * 4);
				padding: calc($gutter * 1.5) calc($gutter * 2.5) $gutter 0;
			}
		}

		&.political .section,
		&.digital .section {
			margin-bottom: 0;
		}

		&.latin-america {
			background-color: $in-focus-prime;
			border: none;
			box-shadow: none;
			margin-top: 0;

			.title-container {
				text-align: left;
			}
		}

		>.foreground {
			max-width: 900px;
		}

		.chapter {
			&.chapter-title {
				background-repeat: no-repeat;
				background-size: auto 100%;
				background-position: center top;
				color: $white;
				font-family: $roman-normal-bold;
				font-size: 1.5rem;
				min-height: 94vh;
				padding: calc($gutter * 6);
				position: relative;
				-webkit-box-shadow: inset 0 -100px 60px -30px $white;
				box-shadow: inset 0 -100px 60px -30px $white;

				.title-container {
					background-color: rgba(255, 255, 255, 0.6);
					color: #1A4A5E;
					padding: calc($gutter * 2) calc($gutter * 2) calc($gutter * 1.5);
					position: relative;
					text-align: center;
					margin-top: 22vh;
					width: auto;
				}

				h2 {
					color: $proj-comp;
					display: inline-block;
					font-family: $roman-normal-bold;
					font-size: 4.5rem;
					line-height: 0.9em;
					padding-bottom: $gutter;
					// text-shadow: 2px 2px 5px rgba(255,255,255, 1);
					text-transform: uppercase;
				}

				p {
					font-size: 1.6rem;
					text-align: left;
					// text-shadow: 2px 2px 3px $proj-comp-alt;
				}
			}

			&.chapter-title.political-levers .title-container {
				margin-top: 0;
			}

			&.top-section {
				color: $proj-comp-alt;
				font-family: $roman-normal-book;
				font-size: 2rem;
				line-height: 2em;

				.foreground {
					padding: calc($gutter * 2) 0 calc($gutter * 4) 0;
				}

				h3 {
					color: $proj-comp;
					font-family: $roman-normal-bold;
					font-size: 4rem;
					line-height: 0.9em;
					margin-bottom: calc($gutter * 2);
					text-align: center;
					text-transform: uppercase;
				}

				.icon {
					background-size: 300px 300px;
					margin: 0 auto 120px auto;
					background-repeat: no-repeat;
					background-position: top center;
					padding-top: 350px;
				}

				&.casestudy--venezuela {
					.icon-chapter-top {
						background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-chaptertop-economic.png);
					}
				}

				&.casestudy--hungary {
					.icon-chapter-top {
						background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-chaptertop-political.png);
					}
				}

				&.casestudy--eu {
					.icon-chapter-top {
						background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-chaptertop-info.png);
					}
				}

				&.casestudy--mena {
					.icon-chapter-top {
						background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-chaptertop-military.png);
					}
				}
			}

			&.economic-levers {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/chapter-title-econ-background.jpg);
			}

			&.political-levers {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/chapter-title-political-background-2.jpg);
			}

			&.information-space {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/chapter-title-info-background.jpg);
			}

			&.military-security {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/chapter-title-military-background.jpg);
			}

			&.chapter-title.latin-america {
				background-attachment: fixed;
				background-color: $in-focus-prime;
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/latam-intro-bkgrnd.gif);
				background-position: center 6vh;
				background-size: auto 100%;
				box-shadow: none;
				-webkit-box-shadow: inset 0 -60px 60px 0px $in-focus-prime;
				box-shadow: inset 0 -60px 60px 0px $in-focus-prime;
				padding-top: calc($gutter * 13);

				.title-container {
					background-color: transparent;
					color: $white;
					margin: 0 auto;
					padding: 0;
					text-align: left;
					width: 70%;
				}

				strong {
					display: block;
					float: none;
					margin-bottom: $gutter;
					text-transform: uppercase;
				}

				h2 {
					color: $proj-accent;
					float: none;
					font-size: 8rem;
					line-height: .8em;
					margin-bottom: calc($gutter * 10);
				}

				p {
					font-size: 2.2rem;
					line-height: 2em;
					margin-bottom: calc($gutter * 2);

				}
			}
		}

		&.tool-chapter--history .chapter.chapter-title .title-container {
			margin-top: 0;
		}
	}

	.photo-section {
		background-color: $proj-comp-alt;
		margin-bottom: calc($gutter * 2);

		&#caseStudyStart>div {
			background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/slideshow-bg-economic.png);
			background-repeat: repeat;
			background-size: 20%;
			display: inline-block;
			position: relative;
		}


		&#caseStudyStartHungary>div {
			background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/slideshow-bg-political.png);
			background-repeat: repeat;
			background-size: 20%;
			display: inline-block;
			position: relative;
		}

		&#caseStudyStartEU>div {
			background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/slideshow-bg-info.png);
			background-repeat: repeat;
			background-size: 20%;
			display: inline-block;
			position: relative;
		}

		&#slideMENAContainerBackground>div {
			background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/slideshow-bg-military.png);
			background-repeat: repeat;
			background-size: 20%;
			display: inline-block;
			position: relative;
		}

		.case-study-container-background {
			display: block;
			position: relative;
		}

		.case-study-slides {
			overflow: hidden;
			padding: 10% 0;
		}

		.item {
			//background-color: $black;
			display: inline;
			float: left;
			height: 100vh;
			position: relative;
			width: 100vw;

			.image-container,
			.quote {
				// display: table;
				float: left;
				height: 70%;
				margin-left: 5%;
				position: relative;
				width: 70%;

				.img {
					background-position: center right;
					background-repeat: no-repeat;
					background-size: 100% auto;
					-webkit-border-radius: 10px;
					border-radius: 10px;
					-webkit-box-shadow: inset 0 0 100px 0 #000000;
					box-shadow: inset 0 0 100px 0 #000000;
					display: table-cell;
					float: left;
					height: 100%;
					vertical-align: middle;
					width: 100%;
				}

				&.no-image {
					border-right: none;
					color: $white;
					display: table;
					font-size: 2.5rem;
					margin: 0 15%;
					position: relative;
					text-align: center;
					width: 70%;

					>span {
						border-top: 5px solid $proj-comp;
						border-bottom: 5px solid $proj-comp;
						display: table-cell;
						position: relative;
						vertical-align: middle;
					}

					a {
						color: $white;
						text-decoration: underline;
					}
				}
			}

			.image img,
			.image iframe,
			.image .quote {
				float: left;
				margin-left: $gutter;
				width: 70%;

				//-for tablet slim
				// float: none;
				// width: 100%;
			}



			&.embedded-video {
				.image {
					float: none;
					max-width: 80%;
				}
			}
		}

		.slide-content {
			color: $white;
			display: table;
			height: 100%;
			float: left;
			font-family: $roman-normal-bold;
			font-size: 2.5rem;
			line-height: 1.5em;
			margin: 0 50px 0 -10%;
			position: relative;
			width: 30%;


			em {
				font-family: $roman-italic-bold;
			}

			a {
				color: $white;
				text-decoration: underline;
			}

			&.quote {
				color: $white;
				font-family: $roman-italic-bold;
				font-size: 3rem;
				padding: 1.5em 1em;
				position: relative;
				text-shadow: 3px 3px 3px $black;
				top: -70%;

				.attribution {
					float: right;
					font-family: $roman-normal-book;
					font-size: 1.6rem;
					line-height: 1.3em;
					padding-top: $gutter;
				}

				.cyrillic {
					// font-family: $cyrillic-normal-book;
					font-size: 0.6em;
					opacity: 0.7;
				}

				.english {
					padding-top: .5em;
				}

				a {
					color: $white;
					text-decoration: underline;
				}
			}

			.highlight {
				background-color: $proj-accent;
			}

			.content-wrap {
				display: table-cell;
				vertical-align: middle;

				>span {
					background-color: $proj-comp;
					display: block;
					padding: $gutter;
				}
			}

			//-for tablet slim
			// float: none;
			// width: 100%;

		}

		.slide-caption {
			border-top: 1px solid $proj-accent;
			color: $white;
			font-family: $roman-italic-book;
			font-size: 1rem;
			line-height: normal;
			margin-top: calc(calc($gutter / 2));
			padding-top: calc(calc($gutter / 2));
		}

		.item.embedded-video .slide-content {
			float: none;
			height: auto;
			width: 80%;
		}
	}

	.photo-gallery.floating {
		float: left;
		margin: 0 1.5em 0 0;
		width: 60%;

		&.right {
			float: right;
		}
	}

	.card-deck {
		background-color: #a7a9ac;

		&.section {
			padding: $gutter 0 calc($gutter * 1.5) 0;
		}

		.cols .banded {
			background-color: $blue-main;
		}

		.cols .padded-less {
			padding-top: 0;
		}

		h3 {
			color: $blue-main;
			font-family: $roman-normal-bold;
			font-size: 1.3rem;
			letter-spacing: 0.1em;
			margin-bottom: calc(calc($gutter / 2));
			text-align: center;
			text-transform: uppercase;
		}

		.photo {
			background-position: center top;
			background-repeat: no-repeat;
			background-size: auto calc($gutter * 4);
			padding-top: calc($gutter * 4);
		}

		.card-summary {
			display: none;
		}

		h4 {
			background-color: $blue-main;
			color: $white;
			line-height: 1.1em;
			padding: calc(calc($gutter / 2));
			text-align: center;
		}

		p {
			color: $white;
			font-family: $roman-normal-bold;
			line-height: 1.1em;
			text-align: center;
		}
	}

	.text-slide {
		background-color: $proj-comp;

		&.section {
			margin-bottom: 0;
		}

		&.map_in-focus {
			background-color: $in-focus-prime;
		}

		h3 {
			text-align: center;
			color: $proj-accent;
			display: inline-block;
			font-family: $roman-normal-bold;
			font-size: 2.5rem;
			line-height: 1.1em;
			margin: 8% auto 0;
			position: relative;
			text-align: center;
			text-transform: uppercase;
			width: 100%;
			z-index: 99;
		}

		.slide {
			background-position: center bottom;
			background-repeat: no-repeat;
			background-size: 100% auto;
			height: 100vh;
			width: 100%;
		}

		&.fx--toolkit-history.politics-slide {
			background-color: $proj-prime;

			.video h3 {
				margin-top: 100px;
				padding-bottom: calc(calc($gutter / 2));
			}
		}

		&.fx--toolkit-history.digital-slide {
			background-color: $proj-comp-alt;
		}

		.callout-text {

			em,
			i {
				font-family: $roman-italic-book;
				font-variant: normal;
			}
		}
	}

	#energyStick {
		.slide {
			height: 100vh;
		}
	}

	.map {
		background-color: $proj-comp;

		&.section {
			margin-bottom: 0;
		}

		&.map_in-focus {
			background-color: $in-focus-prime;
		}

		h3 {
			text-align: center;
			color: $proj-accent;
			display: inline-block;
			font-family: $roman-normal-bold;
			font-size: 2.5rem;
			line-height: 1.1em;
			margin: 8% auto 0;
			position: relative;
			text-align: center;
			text-transform: uppercase;
			width: 100%;
			z-index: 99;
		}

		.slide {
			background-position: center bottom;
			background-repeat: no-repeat;
			background-size: 100% auto;
			height: 100vh;
			width: 100%;
			opacity: 0;
			position: absolute;
			top: -100vh;
		}

		&.map-captions {
			color: $white;
			font-family: $roman-normal-bold;
			font-size: 1rem;
			line-height: 1.6em;
			padding: $gutter;
			position: relative;
			margin-top: 0;
			margin-bottom: 0;

			h4 {
				border-bottom: 4px solid white;
				color: $white;
				font-family: $roman-normal-bold;
				font-size: 2rem;
				margin-bottom: 15px;
				margin-left: 10%;
				padding-bottom: 5px;
				padding-top: $gutter;
				text-transform: uppercase;
				width: 50%;

				span {
					color: $proj-accent;
					display: block;
					font-size: 0.8rem;
				}
			}

			p {
				line-height: 1.1em;
				margin-bottom: calc(calc($gutter / 3));
				margin-left: 10%;
				width: 80%;

				&.not-reported {
					font-family: $roman-italic-book;
				}

				&.source {
					font-family: $roman-italic-book;
					text-align: right;

					a {
						color: $white;
						cursor: pointer;
						text-decoration: underline;
					}
				}
				strong {float: none;}
			}

			strong {
				color: $proj-accent;
				float: left;
				font-family: $roman-normal-bold;
				font-weight: normal;
				line-height: 1.4em;
				text-indent: -4em;
				text-transform: uppercase;
			}
		}

		.map-default {
			background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-start_no-kremlin.gif);
		}

		&.map-energy {
			.caption-container {
				left: 12vw;
				top: 30vh;

				p {
					margin-left: 0;
				}
			}
		}

		&.map-arms {
			.arms-slide9 {

				.caption-container {
					background-color: transparent;
					color: $white;
					display: inline-block;
					font-family: $roman-normal-bold;
					font-size: 3rem;
					left: 0;
					line-height: 1.1em;
					padding: 0;
					position: relative;
					text-align: center;
					top: 64vh;
					width: 100%;

					p {
						display: inline-block;
						font-size: 2.4rem;
						line-height: 1.1em;
						text-shadow: 0 0 5px #C73E1D;
						margin: 0;
						width: 80%;

						a {
							color: $white;
							text-decoration: underline;
						}
					}
				}
			}
		}

		&.map-cultivation {
			.caption-container {
				background-color: transparent;
				color: $white;
				display: inline-block;
				font-family: $roman-normal-bold;
				font-size: 3rem;
				left: 0;
				line-height: 1.1em;
				padding: 0;
				position: relative;
				text-align: center;
				top: 64vh;
				width: 100%;

				p {
					display: inline-block;
					line-height: 1.1em;
					margin: 0;
					text-align: left;
					width: 60%;
				}
			}

			.map-default {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/political-graphic-1.gif);
			}

			.cult-slide1 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/political-graphic-2.gif);
			}

			.cult-slide2 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/political-graphic-3.gif);
			}

			.cult-slide3 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/political-graphic-4.gif);
			}

			.cult-slide4 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/political-graphic-5.gif);

				p {
					text-align: center;
				}
			}

			.cult-slide5 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/political-graphic-6.gif);

				p {
					text-align: center;
				}
			}

			// .cult-slide6 {background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/political-graphic-6.gif);}
		}

		&.map-la{
			margin-top: 0;
			padding-top: 30px;
			.slide {
				background-position: center 8vh;
				top: -100vh;
			}

			h3 {
				color: $white;
			}

			.map-default {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/map-latam-start.gif);
			}

			.caption-container {
				background-color: transparent;
				font-size: 1.1rem;
				position: absolute;
				top: 30vh;
				width: 25em;
				// z-index: 1;

				&::after {
					display: none;
				}

				// &.first {top: 26vh;}
				&.secondary-container {
					background-color: rgba(0, 0, 0, 0.3);
					display: block;
					left: 70vw;
					padding-bottom: 0;
					top: 15vh;
					width: 20em;
				}

				p {
					line-height: normal;

					a {
						color: $white;
						text-decoration: underline;
					}
				}
			}

			

			.latin-america-slide5 .caption-secondary {
				font-size: 0.8rem;
			}

			.latin-america-slide7 .caption-container p {
				margin-left: 0;
			}

			.latin-america-slideEnd {
				.caption-container {
					background-color: transparent;
					color: $white;
					display: inline-block;
					font-family: $roman-normal-bold;
					font-size: 3rem;
					left: 0;
					line-height: 1.1em;
					padding: 0;
					position: relative;
					text-align: center;
					top: 50vh;
					width: 100%;

					p {
						display: inline-block;
						font-size: 2.4rem;
						line-height: 1.1em;
						text-shadow: 0 0 5px #C73E1D;
						margin: 0;
						width: 80%;

						a {
							color: $white;
							text-decoration: underline;
						}

						&.main-cta {
							font-size: 2rem;
							text-align: center;

							a {
								background-color: $proj-comp-alt;
								color: $proj-accent;
								display: block;
								font-size: 3rem;
								margin: $gutter auto 0 auto;
								padding: $gutter;
								text-decoration: none;
								text-transform: uppercase;
								text-align: center;
								width: 50%;

								&:hover {
									color: $white;
								}
							}


						}
					}
				}
			}
		} 
	}
	#latin-americaStick {margin-top: 0}
}

#latin-americaStick {
	height: 100vh;
	.slide {height: 100%}
}
.latAm-captions, .map-la {
	background-color: #A23B72;
	margin-bottom: 0;
	margin-top: 0;
	padding-bottom: 20px;

	h3 {
		color: #F18F01;
		font-size: 4rem;
		text-align: center;
		padding-bottom: 30px;
	}

	h4 {
		border-bottom: 4px solid white;
		color: #fff;
		font-family: "Frutiger Neue LT W06 Bold","Arial Black",Gadget,sans-serif;
		font-size: 2rem;
		margin-bottom: 15px;
		padding-bottom: 5px;
		text-transform: uppercase;
		width: 70%;
	}

	p {
		color: $white;
		font-size: 2rem;

		&.main-cta {
			font-size: 2rem;
			text-align: center;

			a {
				background-color: $proj-comp-alt;
				color: $proj-accent;
				display: block;
				font-size: 3rem;
				margin: $gutter auto 0 auto;
				padding: $gutter;
				text-decoration: none;
				text-transform: uppercase;
				text-align: center;
				width: 50%;

				&:hover {
					color: $white;
				}
			}


		}
	}
	.caption-secondary {
		font-family: $roman-normal-book;
		font-size: 0.9rem;
		padding-bottom: 30px;

		p {
			background-position: left top;
			background-repeat: no-repeat;
			background-size: calc($gutter * 2);
			line-height: 1.2em;
			margin-bottom: $gutter;
			margin-top: $gutter;
			margin-left: 0;
			padding-left: calc($gutter * 2.5);

			>strong {
				color: #F18F01;
				display: block;
				float: none;
				text-indent: unset;
			}

			&.economic {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-toolnav-economic.png);
			}

			&.political {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-toolnav-political.png);
			}

			&.info {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-toolnav-information.png);
			}

			&.military {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-toolnav-military.png);
			}
		}
	}

}

:deep(.timeline-news) {
	background-color: $proj-comp;
	margin-bottom: $gutter;

	h3 {
		background-color: $proj-comp;
		color: $proj-accent;
		font-family: $roman-normal-bold;
		font-size: 4.5rem;
		line-height: 1em;
		padding: calc(calc($gutter / 2)) 0;
		text-align: center;
		text-transform: uppercase;
		width: 100%;
		z-index: 99;

		&.active {
			position: fixed;
			top: 0px;
		}
	}

	.frst-timeline-style-8 {
		&.active {
			padding-top: calc($gutter*9);
		}

		h2 {
			color: $white;
			font-family: $roman-normal-bold;
			font-size: 2.5rem;
			line-height: 1.1em;
			margin-top: 0;
		}

		.details {
			display: block;
			font-family: $roman-italic-bold;
			font-size: 1rem;
		}

		.media-section {
			display: none;
		}

		.frst-timeline-block {
			padding-bottom: 30px;
			padding-left: 45px;
			padding-right: 45px;
			position: relative;
			min-height: 16px;
		}

		.frst-labels {
			padding: 20px 0;
		}

		.frst-timeline-content {
			background-color: transparent;
			border: none;
			color: $white;
		}

		.frst-timeline-img {
			display: none;
		}

		.frst-timeline-content-inner {
			border: none;
			padding: 0;
		}

		.frst-labels {
			span {
				background-color: $proj-comp-alt;
				color: $white;
				font-family: $roman-normal-bold;
				font-size: 2rem;
				left: 0;
				right: 0;
				position: relative;
				color: #fff;
				font-weight: 800;
				padding: 8px 30px;
				border-radius: 4px;
				text-transform: uppercase;
				text-align: center;
			}
		}

		.frst-timeline-block::before {
			background-color: $proj-comp-alt;
			margin-left: -5px;
			width: 11px;
			top: 12px;
			content: "";
			position: absolute;
			height: 100%;
		}

		.frst-timeline-block::after {
			content: "";

			display: table;

			clear: both;

		}

		.frst-timeline-style-8 .frst-timeline-content-inner {
			position: relative;
			border-radius: 4px;
		}

		.frst-timeline-style-8 .frst-timeline-content-inner::before {
			top: 10px;
		}

		@media(min-width: 679px) {
			&.frst-alternate .frst-timeline-block::before {
				left: 50%;
			}

			&.frst-alternate .frst-timeline-label-block {
				text-align: center;
			}

			&.frst-alternate .frst-timeline-block.frst-odd-item .frst-timeline-content,
			&.frst-alternate.frst-date-opposite .frst-timeline-block.frst-even-item .frst-date,
			&.frst-date-opposite.frst-left-align .frst-date,
			&.frst-right-align {
				text-align: right;
			}

			&.frst-alternate .frst-timeline-block.frst-odd-item .frst-timeline-content {
				float: left;
			}

			&.frst-alternate .frst-timeline-block.frst-odd-item .frst-timeline-content {
				float: left;
			}

			&.frst-alternate .frst-timeline-content,
			&.frst-date-opposite .frst-timeline-content {
				width: calc(50% - 44px);
			}

			&.frst-alternate .frst-odd-item .frst-timeline-content-inner::before {
				left: auto;
				right: -11px;
			}

			&.frst-alternate .frst-odd-item .frst-timeline-content-inner::after {
				right: -8px;
				left: auto;
			}

			&.frst-alternate .frst-timeline-block.frst-even-item .frst-timeline-content {
				float: right;
			}
		}

	}

	&.frst-alternate .frst-odd-item .frst-timeline-content-inner::before,
	.frst-timeline-content-inner::before,
	.frst-timeline-content-inner::after {
		border: none;
	}
}

.tool-chapter .panel-container .chapter.history {
	background-color: $white;
	opacity: 1;
	z-index: 99;

	.introduction {
		padding: calc($gutter*7) 0 calc($gutter*8) 0;
		// height: 120vh; (don't need this)

		.icon {
			background-size: 100% auto;
			height: 300px;
			margin: 0 auto;
			width: 300px;

			&.icon-tools-top {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/icon-tools-intro1.png);
				margin-bottom: calc($gutter*4);
			}
		}

		p {
			color: $proj-comp-alt;
			font-family: $roman-normal-book;
			font-size: 2.5em;
			line-height: 2em;
			// margin-right: 9em;
		}

		strong {
			color: $proj-comp-alt;
			font-family: $roman-normal-book;
			font-size: 2.7em;
			line-height: 2.2em;
			margin-bottom: calc($gutter*3);
		}
	}
}

.section.toolkit-history {
	padding: calc($gutter*2) 0;
	position: relative;
	z-index: 99;

	.callout-text {
		display: block;
		margin: 0 auto 0;
		width: 70%;

		.content-wrap {
			color: $proj-comp;
			font-family: $roman-normal-bold;
			font-size: 3.5rem;
			margin: 40vh 0 10vh;
			text-align: left;

			p {padding-bottom: calc($gutter*1.5); font-size: 3rem}
		}
	}

	ul {
		color: $proj-comp;
		font-size: 1.5em;
	}

	&.intro {background: $white;}

	&.conclusion {
		background: $white;
		height: auto;
		margin-bottom: 0;

		.main-cta {
			background-color: $proj-comp;
			color: $white;
			display: block;
			font-family: $roman-normal-book;
			line-height: normal;
			margin: 0 auto;
			padding: calc(calc($gutter / 2));
			text-align: center;
			width: 50%;

			strong {
				display: block;
				font-family: $roman-normal-bold;
				font-size: 2.5rem;
				font-weight: normal;
				text-transform: uppercase;
			}

			&:hover {
				background-color: $proj-prime;
				text-decoration: none;
			}
		}
	}
}

.tool-chapter .fx--toolkit-history.text-slide {
		background-color: $proj-prime;

		.slide {
			opacity: 1;
			z-index: 60;
			&.slide1 {
				.callout-text {width: 70%;}

				h3 {
					font-size: 4rem;
					line-height: 0.9em;
					margin-bottom: $gutter;
				}

				p {
					font-size: 2rem;
					padding-top: $gutter;
				}
			}

			h3 span.lowercase {
				background-color: transparent;
				display: inline;
				padding: 0;
				text-transform: none;
			}	
		}

		.callout-text {
			display: table;
			height: 100vh;
			margin: 0 auto 0;
			width: 60%;
			z-index: 99;

			> .content-wrap {
				color: $white;
				display: table-cell;
				padding: 0 15%;
				vertical-align: middle;
				.source {
					font-family: $roman-italic-book;
					text-align: right;
					font-size: 1em;

					a {
						color: $white;
						cursor: pointer;
						text-decoration: underline;
					}
				}
	        }
		}

		.no-text .callout-text > .content-wrap {
			vertical-align: bottom;
			span {margin-bottom: 1em;}
		}

		h3 {margin-top: 0;}

		.video, .doc {
			.callout-text {
				width: 90%;

				.content-wrap {padding: 0;}
			}

			.text {
				display: table;
				height: 60%;
				float: right;
				font-family: $roman-normal-bold;
				font-size: 1.2rem;
				line-height: normal;
				margin: 0 0 0 -10%;
				position: relative;
				width: 30%;

				.content-wrap {
					display: table-cell;
					vertical-align: middle;

					span {
						background-color: $proj-comp;
						display: block;

						padding: 30px;
					}

					a {
						color: white;
						text-decoration: underline;
					}
				}
			}

			h3 {padding-bottom: calc($gutter*1.5);}

			.image-container {
				// display: table;
			    float: left;
			    height: 60%;
			    // margin-left: 5%;
			    position: relative;
			    width: 75%;
			}

			.slide-caption {
				border-top: 1px solid $proj-accent;
				color: $white;
				font-family: $roman-italic-book;
				font-size: 1rem;
				line-height: normal;
				margin-top: calc(calc($gutter / 2));
				padding-top: calc(calc($gutter / 2));
			}
		}

		.full-image {
			.content-wrap span {
				background-color: rgba(0,0,0,0.7);
				display: block;
				padding: $gutter;
			}

			.slide-caption {
				border-top: 1px solid $white;
				color: $white;
				font-family: $roman-italic-book;
				font-size: 0.9rem;
				line-height: normal;
				margin-top: $gutter;
				padding-top: calc(calc($gutter / 2));
			}

			h3 {
				line-height: 0.9em;
				margin-bottom: $gutter;
			}
		}

		.videoEmbed {
			border-bottom: none;
			padding-bottom: 50%;
		}
		
		.politics {
			&.slide {
				background-color: $proj-prime;
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/slideshow-bg-political.png);
				background-repeat: repeat;
				background-size: auto 30%;
			}	

			&.slide2 {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/politics-history-GettyImages-861955902.jpg);
				background-blend-mode: luminosity;
				background-repeat: no-repeat;
				background-size: 100% auto;
			}

			&.slide3 .text {font-size: 1.8rem;}

			&.slide5 {
				background-image: url(http://otvaga2004.ru/wp-content/uploads/2015/05/otvaga2004_sovetnik_06.jpg);
				background-blend-mode: luminosity;
				background-position: top center;
				background-repeat: no-repeat;
				background-size: 100% auto;
				margin-bottom: 0;
			}
		}

		.digital {
			&.slide {
				background-color: $proj-comp-alt;
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/slideshow-bg-info.png);
				background-repeat: repeat;
				background-size: auto 30%;
				margin-bottom: 0;
				margin-top: 0;

				p {font-size: 2.8rem;}
				.content-wrap {font-size: 1.7rem}
			}

			&.slide3 {
				.text {font-size: 1rem;}
			}

			&.slide4 {margin-bottom: 0;}
		}

		.economic {
			margin-bottom: 0;
			margin-top: 0;

			p {font-size: 2.8rem;}
			.content-wrap {font-size: 1.7rem}
			h3 {color: $proj-prime;}

			&.slide {
				background-color: $proj-accent;
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/slideshow-bg-economic.png);
				background-repeat: repeat;
				background-size: auto 30%;
			}

			&.slide2 {
				margin-bottom: 0;

				.text {font-size: 1rem;}
			}
		}

		.military {
			margin-bottom: 0;
			margin-top: 0;

			p {font-size: 2.8rem;}
			.content-wrap {font-size: 1.7rem}
			&.slide {
				background-color: $proj-prime;
				background-image: url(https://ceipimg.s3.amazonaws.com/static/global-russia/slideshow-bg-military.png);
				background-repeat: repeat;
				background-size: auto 30%;
			}
			&.slide2 {
				background-image: url(https://upload.wikimedia.org/wikipedia/ru/thumb/8/80/%D0%9B%D0%B5%D1%82%D1%87%D0%B8%D0%BA%D0%B8_%D1%83%D1%87%D0%B0%D1%81%D1%82%D0%BD%D0%B8%D0%BA%D0%B8_%D0%B2%D0%BE%D0%B7%D0%B4%D1%83%D1%88%D0%BD%D0%BE%D0%B3%D0%BE_%D0%B1%D0%BE%D1%8F_4_%D0%BD%D0%BE%D1%8F%D0%B1%D1%80%D1%8F_1936_%D0%B3%D0%BE%D0%B4%D0%B0_%D0%B2_%D0%98%D1%81%D0%BF%D0%B0%D0%BD%D0%B8%D0%B8.jpg/800px-%D0%9B%D0%B5%D1%82%D1%87%D0%B8%D0%BA%D0%B8_%D1%83%D1%87%D0%B0%D1%81%D1%82%D0%BD%D0%B8%D0%BA%D0%B8_%D0%B2%D0%BE%D0%B7%D0%B4%D1%83%D1%88%D0%BD%D0%BE%D0%B3%D0%BE_%D0%B1%D0%BE%D1%8F_4_%D0%BD%D0%BE%D1%8F%D0%B1%D1%80%D1%8F_1936_%D0%B3%D0%BE%D0%B4%D0%B0_%D0%B2_%D0%98%D1%81%D0%BF%D0%B0%D0%BD%D0%B8%D0%B8.jpg);
				background-blend-mode: luminosity;
				background-position: center center;
				background-repeat: no-repeat;
				background-size: 100% auto;
			}
			&.slide3 {
				background-image: url(https://upload.wikimedia.org/wikipedia/commons/thumb/e/e4/%D0%A3%D1%87%D0%B8%D1%82%D0%B5%D0%BB%D1%8F_%D0%B8_%D1%83%D1%87%D0%B5%D0%BD%D0%B8%D0%BA%D0%B8._%D0%A4%D0%BE%D1%82%D0%BE%2C_%D1%81%D0%B4%D0%B5%D0%BB%D0%B0%D0%BD%D0%BD%D0%BE%D0%B5_%D0%B2%D0%B5%D1%81%D0%BD%D0%BE%D0%B9_1965_%D0%B3._%D0%B2_%D0%B7%D0%B5%D0%BD%D0%B8%D1%82%D0%BD%D0%BE-%D1%80%D0%B0%D0%BA%D0%B5%D1%82%D0%BD%D0%BE%D0%BC_%D1%83%D1%87%D0%B5%D0%B1%D0%BD%D0%BE%D0%BC_%D1%86%D0%B5%D0%BD%D1%82%D1%80%D0%B5_%D0%B2%D0%BE_%D0%92%D1%8C%D0%B5%D1%82%D0%BD%D0%B0%D0%BC%D0%B5.jpg/800px-thumbnail.jpg);
				background-blend-mode: luminosity;
				background-repeat: no-repeat;
				background-size: 100% auto;
			}
			&.slide4 {margin-bottom: 0;}
		}
	}


.section.economy-tool, .section.political-tool, .section.military-tool, .section.history-tool {
	margin-top: 0;
	margin-bottom: 0;
	;
}

.section.tools {
	margin-bottom: 0;
}

.conclusion {
	padding: calc($gutter*7) 0 calc($gutter*7) 0;
	z-index: 99;

	p {
		color: $proj-comp-alt;
		font-family: $roman-normal-book;
		font-size: 3em;
		line-height: 2em;
		padding-bottom: 1em;
	}


	.icon {
		background-size: 100% auto;
		height: 300px;
		margin: 0 auto calc($gutter*4) auto;
		width: 300px;
	}
}

.project-questions {
	background-color: $proj-comp;
	color: $white;
	z-index: 99;
	padding-top: calc($gutter*3);
	padding-bottom: calc($gutter*2);
	
	.callout {
		color: $white;
		font-family: $roman-normal-bold;
		text-align: left;

		.callout-text {
			display: block;
			float: none;
			margin: 0 auto 0;
			width: 70%;

			> .content-wrap {
				display: block;
				vertical-align: middle;
				margin-bottom: 30px;
			}
		}				

		em {
			font-family: $roman-normal-bold;
			font-style: normal;
		}
		
		&.callout-first {
			background-color: $proj-comp;
			font-size: 4rem;
			line-height: 1.6em;				
		}
		.cta {
			background-color: $proj-accent;
			border-radius: 5px 5px 5px 5px;
			-webkit-box-shadow: 0 10px 20px -10px #5B1D0E;
			box-shadow: 0 10px 20px -10px #5B1D0E;
			color: $white;

			.button {
				background-color: transparent;
				display: block;
				font-family: $roman-normal-bold;
				font-size: 3.5rem;
				margin-left: 0;
				padding: $gutter calc($gutter*2);
				text-align: center;
				text-transform: uppercase;
			}

			.button::after {display:none;}

			em {
				display: block;
				font-family: $roman-normal-book;
				font-size: 3rem;
				font-style: none;
				text-transform: none;		
			}
		}
	}
}
</style>